/**
 * Profesyonel fatura taslağı — normalizasyon, satır ve toplam hesapları.
 *
 * Özel matrah (KDV 23/e, kod 805) — kuyum uygulaması:
 * - Ödenecek tutar ≈ has altın bedeli (KDV %0) + işçilik net + işçilik KDV %20
 * - Has bedeli: kategori detay tablosu (has çarpanı veya gram × has × milyem)
 * - İşçilik: tutarın kalanı (KDV dahil) / 1.20
 */

import {
  BILEZIK_22_INVOICE_NAME,
  findCatalogRowForIslem,
  isWholeNumberGrams,
  mergeInvoiceProductCatalog,
  parseUnitGramsFromProductName,
  resolveMilyemForInvoiceName,
  TAKI_8_AYAR_INVOICE_NAME
} from './invoiceProductCatalog';
import { resolveInvoiceConditionAction } from './invoiceConditionRules';
import { mergeInvoiceRules, ruleMatches, ruleInputsFromIslem } from './invoiceRules';
import {
  catalogRowHasValue,
  findNearestPricedProduct,
  mergeAmountNearestProductRule
} from './invoiceAmountProductRule';
import { lookupLiveProductPrice, resolveLiveProductPrices } from './invoiceLivePriceTable';
import { getIstanbulDayKey, getIstanbulTimePart } from './bankingDateTime';

export const DEFAULT_INVOICE_NOTE = 'Özel matrah kapsamında altın satışıdır.';
export const OZEL_MATRAH_CODE_DEFAULT = '805';

/** Bir satıra yazılabilecek en büyük yuvarlama artığı (TL). */
export const LINE_ROUNDING_MAX_TL = 0.5;

/**
 * Miktara göre izin verilen yuvarlama artığı.
 * Birim fiyat kuruşa yuvarlandığı için satır toplamındaki sapma en fazla
 * miktar × yarım kuruş kadar olabilir; iki satırda miktar × 1 kuruş. Döviz
 * girişlerinde miktar 2.572,20 gram olabildiği için sabit 0,50 TL yetmiyor.
 * Gerçek bir hesap hatası bu sınırın içinde kalamaz.
 */
export const lineRoundingTolerance = (quantity) =>
  round2(LINE_ROUNDING_MAX_TL + Math.abs(Number(quantity) || 0) * 0.01);

/**
 * Fatura notu — şubeye göre mağaza satırı değişir.
 *
 * Metinler Kolaysoft arşivindeki 11.547 faturadan alındı; yeni kesilen faturalar
 * geçmişle birebir aynı görünsün diye birebir korunuyor:
 *   MERKEZ MAĞAZADAN KESİLMİŞTİR                        (3.536 fatura)
 *   KÜÇÜKÇEKMECE / SEFAKÖY ŞUBE MAĞAZADAN KESİLMİŞTİR   (2.086)
 *   BEYLİKDÜZÜ / KAVAKLI ŞUBE MAĞAZADAN KESİLMİŞTİR     (1.569)
 *   HAVALE / EFT SATIŞ (9.396) · POS SATIŞ (1.325)
 *
 * "Not:" öneki YAZILMIYOR: arşivdeki hiçbir faturanın UBL'inde yok, öneki
 * resmi görünüm katmanı (officialInvoiceModel) gösterirken kendisi ekliyor.
 */
export const BRANCH_INVOICE_STORE_LABELS = {
  sefakoy_merkez: 'MERKEZ MAĞAZADAN',
  sefakoy_sube: 'KÜÇÜKÇEKMECE / SEFAKÖY ŞUBE MAĞAZADAN',
  kavakli_sube: 'BEYLİKDÜZÜ / KAVAKLI ŞUBE MAĞAZADAN'
};

export const buildBranchInvoiceNote = (sube, paymentChannel = 'havale') => {
  const store =
    BRANCH_INVOICE_STORE_LABELS[String(sube || '').trim()] || 'MERKEZ MAĞAZADAN';
  return [
    `${store} KESİLMİŞTİR`,
    paymentChannel === 'krediKarti' ? 'POS SATIŞ' : 'HAVALE / EFT SATIŞ',
    'MÜŞTERİYE MÜESSESEDE TESLİM EDİLDİĞİNDEN SEVK İRSALİYESİ KESİLMİŞTİR'
  ].join('\n');
};

/**
 * Taslak açılırken not alanı ne olmalı.
 *
 * Not alanı PERSONELİNDİR: doluysa aynen korunur. Eskiden "HAVALE / EFT SATIŞ
 * geçmiyorsa yeniden üret" deniyordu; personel notu "POS SATIŞ" diye düzeltip
 * kaydedince taslak her açılışta eski hâline dönüyordu (13.10.2026 geri
 * dönüşü). Yalnızca boş notu, ödeme tipine uygun şekilde sistem doldurur.
 */
export const resolveDraftNoteOnOpen = (mevcutNot, sube, paymentType = '') => {
  const not = String(mevcutNot || '').trim();
  if (not) return String(mevcutNot);
  const kartOdemesi = String(paymentType || '').toLocaleLowerCase('tr-TR').includes('kart');
  return buildBranchInvoiceNote(sube, kartOdemesi ? 'krediKarti' : 'havale');
};

export const INVOICE_CUSTOMER_TYPES = [
  { value: 'sahis', label: 'Şahıs' },
  { value: 'firma', label: 'Firma' },
  { value: 'yabanci', label: 'Yabancı' }
];

export const INVOICE_SCENARIO_TYPES = [
  { value: 'eArsiv', label: 'e-Arşiv' },
  { value: 'eFatura', label: 'e-Fatura' }
];

export const INVOICE_TYPE_OPTIONS = [
  { value: 'ozelMatrah', label: 'Özel Matrah' },
  { value: 'satis', label: 'Satış' },
  { value: 'ticari', label: 'Ticari' },
  { value: 'istisna', label: 'İstisna' }
];

export const INVOICE_SENDING_TYPES = [
  { value: 'elektronik', label: 'Elektronik' },
  { value: 'kagit', label: 'Kağıt' }
];

export const INVOICE_UNIT_OPTIONS = ['ADET', 'GRAM'];

export const DEFAULT_PROFESSIONAL_DRAFT = {
  customerType: 'sahis',
  country: 'Türkiye',
  title: '',
  vknTckn: '',
  taxOffice: '',
  city: '',
  district: '',
  address: '',
  scenarioType: 'eArsiv',
  /**
   * GİB mükellef sorgusu sonucu (EDM CheckUser).
   * { isEfatura, alias, title, unit, identifier, checkedAt, testMode }
   * Gönderim anında yeniden sorgulanır; bu alan personelin onaydan önce doğru
   * senaryoyu görmesi ve eksik alanın erkenden yakalanması içindir.
   */
  recipientCheck: null,
  invoiceType: 'ozelMatrah',
  sendingType: 'elektronik',
  invoiceTime: '12:00',
  currency: 'TRY',
  xsltTemplate: 'gibXslt',
  isWaybillReplacement: false,
  invoiceNote: DEFAULT_INVOICE_NOTE,
  paymentType: 'Havale/EFT',
  matrahType: 'Özel Matrah',
  /** Manuel ürün tipi: tamir | 22taki | 22gram | 24gram */
  productTypeOverride: ''
};

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

export const BRANCH_INVOICE_LOCATIONS = {
  kavakli_sube: { city: 'İstanbul', district: 'Beylikdüzü' },
  sefakoy_merkez: { city: 'İstanbul', district: 'Küçükçekmece' },
  sefakoy_sube: { city: 'İstanbul', district: 'Küçükçekmece' }
};

export const getDefaultBranchInvoiceLocation = (sube) =>
  BRANCH_INVOICE_LOCATIONS[String(sube || '').trim()] || { city: '', district: '' };

export const TAKI_22_AYAR_INVOICE_NAME = '22 Ayar Takı';
export const ALTIN_TAMIR_UCRETI_NAME = 'Altın Tamir Ücreti';
export const GRAM_24_INVOICE_NAME = '24 Ayar Gram Altın';
export const GRAM_22_INVOICE_NAME = '22 Ayar Gram Altın';
export { BILEZIK_22_INVOICE_NAME, TAKI_8_AYAR_INVOICE_NAME };
/** Giriş işlemleri: bu tutarın altı → Altın Tamir Ücreti (ayarlarla override). */
export const GIRIS_INVOICE_THRESHOLD = 3000;
const MILYEM_22_AYAR_DEFAULT = 0.916;
/** Tamir faturasında işçilik satırının varsayılan birim fiyatı (KDV hariç). */
const TAMIR_ISCILIK_NET_DEFAULT = 100;
/** Ürünü değiştirilen faturada işçiliğe ayrılan pay (%, KDV dahil). */
const CEVRILEN_ISCILIK_YUZDESI_DEFAULT = { '8taki': 10, '14taki': 10, '22taki': 1, '22bilezik': 1 };

const getProductCatalog = (settings) => mergeInvoiceProductCatalog(settings?.productCatalog);

/** Adet × birim gram; işlemde toplam gram varsa onu korur. */
const resolveTotalGramsForIslem = (islem, unitGramsHint = 0) => {
  const adet = Number(islem?.adet) > 0 ? Number(islem.adet) : 1;
  const gram = Number(islem?.gram) || 0;
  const unitGrams =
    unitGramsHint > 0
      ? unitGramsHint
      : parseUnitGramsFromProductName(islem?.urun) || parseUnitGramsFromProductName(islem?.invoiceSourceUrun);
  if (unitGrams > 0) {
    const expected = round2(adet * unitGrams);
    if (gram > 0 && Math.abs(gram - expected) < 0.051) return expected;
    if (gram > 0 && Math.abs(gram - unitGrams) < 0.051) return expected;
    if (gram > 0 && adet <= 1) return round2(gram);
    if (gram > expected * 0.85) return round2(gram);
    return expected;
  }
  if (gram > 0) return round2(gram);
  return round2(adet);
};

/** Bilezik / takı gibi mücevher — gram altın fatura adına çevrilmez. */
const JEWELRY_PRODUCT_HINT =
  /bilezik|yüzük|yuuzuk|küpe|kupe|kolye|halhal|zincir|set\b|tak[iı]|künye|kunye|bileklik/i;

export const INVOICE_PRODUCT_TYPE_OPTIONS = [
  { value: 'tamir', label: 'Altın Tamir Ücreti' },
  { value: '22taki', label: '22 Ayar Takı' },
  { value: '14taki', label: '14 Ayar Takı' },
  { value: '8taki', label: '8 Ayar Takı' },
  { value: '22bilezik', label: '22 Ayar Bilezik' },
  { value: '22gram', label: '22 Ayar Gram Altın' },
  { value: '24gram', label: '24 Ayar Gram Altın' },
  { value: 'enYakinUrun', label: 'Tutara en yakın ürün' }
];

/**
 * Sarrafiye ürün tipleri (Ürün Tipi menüsü) — katalogdaki has çarpanı TANIMLI
 * Sarrafiye satırlarından üretilir; çarpanı boş olanlar (Beşli, Reşat…) has
 * bedeli hesaplanamadığı için listelenmez. Değer: "sarrafiye:<ürün adı>".
 */
export const SARRAFIYE_TYPE_PREFIX = 'sarrafiye:';

export const getSarrafiyeProductTypeOptions = (settings = null) =>
  mergeInvoiceProductCatalog(settings?.productCatalog)
    .filter((r) => r.category === 'Sarrafiye' && r.mode === 'hasFactor' && Number(r.hasFactor) > 0)
    .map((r) => ({
      value: `${SARRAFIYE_TYPE_PREFIX}${r.productName}`,
      label: r.productName,
      group: 'Sarrafiye',
      hasFactor: Number(r.hasFactor)
    }));

export const getInvoiceProductTypeOptions = (settings = null) => [
  ...INVOICE_PRODUCT_TYPE_OPTIONS,
  ...getSarrafiyeProductTypeOptions(settings)
];

export const isKnownProductTypeOverride = (value, settings = null) => {
  const v = String(value || '').trim();
  if (!v) return false;
  if (INVOICE_PRODUCT_TYPE_OPTIONS.some((o) => o.value === v)) return true;
  return getSarrafiyeProductTypeOptions(settings).some((o) => o.value === v);
};

/**
 * Faturalanacak tutar → seçilen sarrafiyeden kaç adet.
 * Adet, güncel Fiyat Ekranı birim fiyatına göre yuvarlanır; has bedeli tutarı
 * aşmayacak şekilde azaltılır (aşarsa işçilik eksiye düşer). Fiyat tablosu
 * yoksa (acil mod) has bedelinden hesaplanır. En az 1 adet.
 */
export const sarrafiyePiecesForAmount = ({ amount, row, unitPrice = 0, hasFiyati = 0 }) => {
  const tutar = Number(amount) || 0;
  const birimHas = catalogRowHasValue(row, 1, hasFiyati);
  let pieces;
  if (unitPrice > 0) pieces = Math.round(tutar / unitPrice);
  else if (birimHas > 0) pieces = Math.floor(tutar / birimHas);
  else pieces = 1;
  pieces = Math.max(1, pieces || 0);
  while (pieces > 1 && birimHas > 0 && birimHas * pieces > tutar) pieces -= 1;
  return pieces;
};

export const isGramAltinStyleUrun = (urun, kategori = '') => {
  const u = String(urun || '').trim();
  const k = String(kategori || '').trim();
  if (JEWELRY_PRODUCT_HINT.test(u) && !/gram\s*alt[iı]n/i.test(u)) return false;
  if (u === GRAM_24_INVOICE_NAME || u === GRAM_22_INVOICE_NAME) return true;
  if (k === 'Gram Altın') return true;
  if (/\d+([.,]\d+)?\s*gram\s*(22|24)\s*ayar/i.test(u)) return true;
  if (/(22|24)\s*ayar\s*gram(\s*alt[iı]n)?/i.test(u)) return true;
  if (/^(22|24)\s*ayar(\s*alt[iı]n)?$/i.test(u)) return true;
  return false;
};

export const resolveGramAltinInvoiceName = (urun) => {
  const u = String(urun || '').trim();
  if (/24\s*ayar/i.test(u)) return GRAM_24_INVOICE_NAME;
  if (/22\s*ayar/i.test(u)) return GRAM_22_INVOICE_NAME;
  return '';
};

export const getIslemProductName = (islem) => {
  const urun = String(islem?.urun || '').trim();
  const kategori = String(islem?.kategori || '').trim();
  if (
    urun === ALTIN_TAMIR_UCRETI_NAME ||
    urun === TAKI_22_AYAR_INVOICE_NAME ||
    urun === GRAM_24_INVOICE_NAME ||
    urun === GRAM_22_INVOICE_NAME
  ) {
    return urun;
  }
  if (isGramAltinStyleUrun(urun, kategori)) {
    const gramName = resolveGramAltinInvoiceName(urun) || GRAM_24_INVOICE_NAME;
    return gramName;
  }
  if (urun) return urun;
  const productParts = [islem?.kategori, islem?.urun].filter(Boolean);
  return productParts.join(' · ') || islem?.urunBilgisi || '';
};

export const getIslemQuantity = (islem) => {
  const urun = String(islem?.urun || '').trim();
  const kategori = String(islem?.kategori || '').trim();
  if (islem?.invoiceMap?.mode === 'hasFactor') {
    return Number(islem?.invoiceMap?.pieces) || Number(islem?.adet) || 1;
  }
  if (
    urun === GRAM_24_INVOICE_NAME ||
    urun === GRAM_22_INVOICE_NAME ||
    isGramAltinStyleUrun(urun, kategori)
  ) {
    if (Number(islem?.gram) > 0) return Number(islem.gram);
    // Kulce adedi degil GRAMI yazilir. Kolaysoft arsivindeki 3.183 "24 AYAR
    // GRAM ALTIN" satirinin birim fiyati her zaman GRAM fiyatidir (en yuksek
    // ~6.500 TL) ve miktar kesirli olabiliyor (2,5 · 13,5 · 0,5). "5 Gram
    // 24 Ayar x 1 adet"i 1 adet @34.056 TL yazinca arsivdeki hicbir faturaya
    // benzemiyordu; dogrusu 5 adet @6.811 TL.
    const adet = Number(islem?.adet) || 1;
    const birimGram = parseUnitGramsFromProductName(urun);
    return birimGram > 0 ? round2(adet * birimGram) : adet;
  }
  if (kategori === 'Takı Ürün' || kategori === 'Hurda' || urun === BILEZIK_22_INVOICE_NAME || urun === TAKI_8_AYAR_INVOICE_NAME) {
    return Number(islem?.gram) || 1;
  }
  if (kategori === 'Sarrafiye') {
    return Number(islem?.adet) || 1;
  }
  if (kategori === 'Gram Altın') {
    if (Number(islem?.gram) > 0) return Number(islem.gram);
    const adet = Number(islem?.adet) || 1;
    const birimGram = parseUnitGramsFromProductName(urun);
    return birimGram > 0 ? round2(adet * birimGram) : adet;
  }
  if (urun === ALTIN_TAMIR_UCRETI_NAME) {
    return 1;
  }
  if (Number(islem?.gram) > 0) return Number(islem.gram);
  if (Number(islem?.adet) > 0) return Number(islem.adet);
  return 1;
};

/**
 * Faturadaki birim. Ölçüt Kolaysoft arşivindeki 11.547 faturadan alındı:
 *   sayılan ürünler (sarrafiye, gram altın külçesi, tamir) → ADET (C62)
 *   tartılan ürünler (takı, hurda/has altın)              → GRAM (GRM)
 * Gram altın 1 gramlık külçe olarak adetle satıldığı için arşivdeki 3.187
 * "24 AYAR GRAM ALTIN" satırının 3.183'ü ADET; eskiden GRAM yazıyorduk.
 */
export const getIslemUnit = (islem) => {
  const urun = String(islem?.urun || '').trim();
  const kategori = String(islem?.kategori || '').trim();
  if (islem?.invoiceMap?.mode === 'hasFactor' || kategori === 'Sarrafiye') {
    return 'ADET';
  }
  if (
    urun === GRAM_24_INVOICE_NAME ||
    urun === GRAM_22_INVOICE_NAME ||
    isGramAltinStyleUrun(urun, kategori)
  ) {
    return 'ADET';
  }
  if (kategori === 'Takı Ürün' || kategori === 'Hurda') return 'GRAM';
  if (kategori === 'Gram Altın') return 'ADET';
  if (urun === ALTIN_TAMIR_UCRETI_NAME) return 'ADET';
  if (Number(islem?.gram) > 0) return 'GRAM';
  return 'ADET';
};

export const isDovizSaleIslem = (islem) => {
  const kategori = String(islem?.kategori || '').trim();
  const urun = String(islem?.urun || '').trim();
  return kategori === 'Döviz' && urun && !urun.toUpperCase().startsWith('TL');
};

export const isGirisIslem = (islem) => String(islem?.islemTuru || '').trim() === 'Giriş';

export const isSatisIslem = (islem) => String(islem?.islemTuru || '').trim() === 'Satış';

export const resolveIslemPayableAmount = (islem) =>
  round2(
    Number(islem?.havaleEft) ||
      Number(islem?.toplamTutar) ||
      (Number(islem?.nakit) || 0) + (Number(islem?.havaleEft) || 0) + (Number(islem?.krediKarti) || 0) ||
      0
  );

/** 22 Ayar Takı gram birim satış fiyatı (TL/gr) — ayarlar veya işlem has fiyatından. */
export const resolve22AyarTakiUnitPricePerGram = (islem, settings = null) => {
  const configured = Number(settings?.price22AyarTakiPerGram);
  if (configured > 0) return round2(configured);

  const urun = String(islem?.urun || '');
  const gram = Number(islem?.gram) || 0;
  const liste = Number(islem?.listeFiyati) || 0;
  if (urun.includes('22 Ayar') && gram > 0 && liste > 0) {
    return round2(liste / gram);
  }

  const hasFiyati = Number(islem?.hasFiyati) || 0;
  if (hasFiyati > 0) {
    const milyem = Number(settings?.milyem22Ayar) || MILYEM_22_AYAR_DEFAULT;
    const iscilikPerGram = Number(settings?.iscilik22AyarPerGram) || 0;
    return round2(hasFiyati * milyem + iscilikPerGram);
  }

  return 0;
};

/** Milyeme göre gram tahmini — has fiyatı × milyem birim fiyat kabul edilir. */
export const gramFromAmountForMilyem = (islem, milyem, tutar = null) => {
  const amount = tutar != null ? round2(Number(tutar) || 0) : resolveIslemPayableAmount(islem);
  const has = Number(islem?.hasFiyati) || 0;
  const unit = has * (Number(milyem) || 0);
  if (!(amount > 0) || !(unit > 0)) return 0;
  return round2(amount / unit);
};

export const gramFromAmountFor22AyarTaki = (islem, settings = null, tutar = null) => {
  const amount = tutar != null ? round2(Number(tutar) || 0) : resolveIslemPayableAmount(islem);
  const unit = resolve22AyarTakiUnitPricePerGram(islem, settings);
  if (!(amount > 0) || !(unit > 0)) return 0;
  return round2(amount / unit);
};

/** Has fiyatından gram tahmini (24/22 ayar gram altın). */
export const gramFromAmountForHasGold = (islem, settings = null) => {
  const amount = resolveIslemPayableAmount(islem);
  const withHas = enrichIslemWithLiveHasPrice(islem, settings?.liveHasSellPrice);
  const has = Number(withHas?.hasFiyati) || 0;
  if (Number(islem?.gram) > 0 && !(has > 0 && amount > 0)) {
    return round2(Number(islem.gram));
  }
  if (!(amount > 0) || !(has > 0)) return Number(islem?.gram) || 0;
  const pct = getWorkmanshipPercentFromSettings(settings, 'Gram Altın');
  const workmanshipGross = round2(amount * (pct / 100));
  const goldPart = round2(Math.max(0, amount - workmanshipGross));
  return round2(goldPart / has);
};

/**
 * Gram altın TAM ADET satılır (1, 2, 3… — 1,8 olamaz). Tutardan hesaplanan
 * gram en yakın tam sayıya yuvarlanır; has bedeli (gram × has × milyem)
 * tutarı aşarsa işçilik eksiye düşmesin diye bir azaltılır. En az 1.
 */
export const wholeGramAltinFromAmount = (islem, milyem, settings = null) => {
  const ham = gramFromAmountForHasGold(islem, settings);
  if (!(ham > 0)) return ham;
  const amount = resolveIslemPayableAmount(islem);
  const has = Number(enrichIslemWithLiveHasPrice(islem, settings?.liveHasSellPrice)?.hasFiyati) || 0;
  const birimHas = has * (Number(milyem) || 1);
  // Adet, tutarın kaç gramlık has bedeline denk geldiğine göre yuvarlanır.
  let adet = Math.max(1, Math.round(birimHas > 0 && amount > 0 ? amount / birimHas : ham));
  // Has bedeli tutarı KÜÇÜK bir farkla aşıyorsa (≤ %1; 13.400 TL'ye 2 gram =
  // 13.402 TL) adet korunur — müşteri 2 gram aldıysa faturada 1 yazılamaz.
  // Belirgin aşımda (10.100 TL'ye 2 gram = 13.402) bir azaltılır.
  while (adet > 1 && birimHas > 0 && amount > 0 && adet * birimHas > amount * 1.01) adet -= 1;
  return adet;
};

const resolveGirisTamirMax = (settings) => {
  const n = Number(settings?.girisTamirMaxTl);
  return Number.isFinite(n) && n >= 0 ? n : GIRIS_INVOICE_THRESHOLD;
};

const resolveGirisGramMin = (settings) => {
  const n = Number(settings?.girisGramAltinMinTl);
  return Number.isFinite(n) && n >= 0 ? n : GIRIS_INVOICE_THRESHOLD;
};

/**
 * Tutara göre fatura tipi — Giriş şartları kuralları.
 * Giriş + döviz (ve benzeri otomatik map) işlemlerde kullanılır.
 */
export const resolveAmountBasedInvoiceAction = (islem, settings = null) => {
  const amount = resolveIslemPayableAmount(islem);
  const fromRules = resolveInvoiceConditionAction(settings, 'giris', amount, {
    partialPayment: isPartialBankPaymentIslem(islem)
  });
  if (fromRules) return fromRules;
  const tamirMax = resolveGirisTamirMax(settings);
  const gramMin = resolveGirisGramMin(settings);
  if (amount < tamirMax) return 'tamir';
  if (amount >= gramMin) return '24gram';
  return null;
};

/** Satışın bir kısmı nakit/kartla ödenmiş, kalanı bankadan gelmişse true. */
export const isPartialBankPaymentIslem = (islem) => {
  const payable = resolveIslemPayableAmount(islem);
  const toplam = Number(islem?.toplamTutar) || 0;
  return payable > 0 && toplam - payable > 1;
};

/** Tutar şartlarının uygulandığı işlemler: Giriş + döviz satış. */
export const isAmountConditionEligibleIslem = (islem) => isGirisIslem(islem) || isDovizSaleIslem(islem);

/** Fatura satırı ürün tipini zorla (14 ayar → 22/24 gram vb.). */
/**
 * Ürün tipi değiştirilirken kayıttaki gram KULLANILABİLİR Mİ?
 *
 * Gram, satılan ürünün AYARINA aittir. 19,61 gramlık 8 ayar takıyı aynı gramla
 * "22 Ayar Bilezik" olarak kesince birim fiyat 3.367 TL çıkıyordu; o gün 22
 * ayar bileziğin gramı 5.986 TL idi. Ayar (milyem) değişiyorsa gram tutardan
 * yeniden hesaplanmalı. Milyem aynıysa (14 Ayar Bilezik → 14 Ayar Takı) gram
 * korunur; yeniden hesaplamak işçiliği ürün bedeline katıp gramı şişiriyor.
 */
const gramForProductOverride = (islem, hedefMilyem, settings, hesapla) => {
  const mevcut = round2(Number(islem?.gram) || 0);
  if (mevcut > 0) {
    const row = findCatalogRowForIslem(islem, getProductCatalog(settings));
    const kaynakMilyem = Number(islem?.invoiceMap?.milyem) || Number(row?.milyem) || 0;
    const hedef = Number(hedefMilyem) || 0;
    if (kaynakMilyem > 0 && hedef > 0 && Math.abs(kaynakMilyem - hedef) < 1e-6) return mevcut;
    if (!(kaynakMilyem > 0)) return mevcut; // ayarı bilinmiyorsa dokunma
  }
  const yeni = round2(Number(hesapla()) || 0);
  return yeni > 0 ? yeni : mevcut;
};

export const applyInvoiceProductTypeOverride = (islem, targetType, settings = null) => {
  if (!islem) return islem;
  const withHas = enrichIslemWithLiveHasPrice(islem, settings?.liveHasSellPrice);
  switch (String(targetType || '').trim()) {
    case 'tamir':
      return {
        ...withHas,
        kategori: 'Sarrafiye',
        urun: ALTIN_TAMIR_UCRETI_NAME,
        adet: 1,
        gram: 0
      };
    case '22taki': {
      const milyem22 = Number(settings?.milyem22Ayar) || MILYEM_22_AYAR_DEFAULT;
      const iscilik22 = gramBasiIscilikNet('22taki', settings);
      const gram = gramForProductOverride(withHas, milyem22, settings, () =>
        iscilik22 > 0
          ? gramFromAmountWithPerGramWorkmanship(withHas, milyem22, iscilik22, settings)
          : gramFromAmountFor22AyarTaki(
              withHas,
              settings,
              round2(resolveIslemPayableAmount(withHas) * altinPayiForOverride('22taki', settings))
            ));
      return {
        ...withHas,
        kategori: 'Takı Ürün',
        urun: TAKI_22_AYAR_INVOICE_NAME,
        gram: gram > 0 ? gram : 0,
        adet: 0,
        invoiceMap: {
          mode: 'milyem',
          milyem: Number(settings?.milyem22Ayar) || MILYEM_22_AYAR_DEFAULT
        }
      };
    }
    // 14 / 8 ayar takı: 22 ayarla aynı mantık, yalnız milyem farklı.
    case '14taki':
    case '8taki': {
      const sekiz = String(targetType).trim() === '8taki';
      const milyem = sekiz ? 0.333 : 0.585;
      /*
       * Gram, tutarın TAMAMINDAN değil altına düşen payından hesaplanır.
       * Tamamından hesaplanınca altın değeri tutara eşitleniyor ve işçilik
       * sıfırlanıyordu: 3.500 TL'lik faturada 3,31 TL işçilik, 55 kuruş KDV.
       */
      const iscilikGr = gramBasiIscilikNet(sekiz ? '8taki' : '14taki', settings);
      const gram = gramForProductOverride(withHas, milyem, settings, () =>
        iscilikGr > 0
          ? gramFromAmountWithPerGramWorkmanship(withHas, milyem, iscilikGr, settings)
          : gramFromAmountForMilyem(
              withHas,
              milyem,
              round2(resolveIslemPayableAmount(withHas) * altinPayiForOverride(sekiz ? '8taki' : '14taki', settings))
            ));
      return {
        ...withHas,
        kategori: 'Takı Ürün',
        urun: sekiz ? '8 Ayar Takı' : '14 Ayar Takı',
        gram: gram > 0 ? gram : 0,
        adet: 0,
        invoiceMap: { mode: 'milyem', milyem }
      };
    }
    case '22bilezik': {
      const milyemBilezik =
        resolveMilyemForInvoiceName(BILEZIK_22_INVOICE_NAME, getProductCatalog(settings)) || 0.916;
      const iscilikBilezik = gramBasiIscilikNet('22bilezik', settings);
      const gram = gramForProductOverride(withHas, milyemBilezik, settings, () =>
        iscilikBilezik > 0
          ? gramFromAmountWithPerGramWorkmanship(withHas, milyemBilezik, iscilikBilezik, settings)
          : gramFromAmountFor22AyarTaki(
              withHas,
              settings,
              round2(resolveIslemPayableAmount(withHas) * altinPayiForOverride('22bilezik', settings))
            ));
      return {
        ...withHas,
        kategori: 'Takı Ürün',
        urun: BILEZIK_22_INVOICE_NAME,
        gram: gram > 0 ? gram : 0,
        adet: 0,
        invoiceMap: {
          mode: 'milyem',
          milyem: milyemBilezik
        }
      };
    }
    case '22gram': {
      const milyem22 = resolveMilyemForInvoiceName(GRAM_22_INVOICE_NAME, getProductCatalog(settings)) || 0.916;
      const gram = gramForProductOverride(withHas, milyem22, settings, () =>
        wholeGramAltinFromAmount(withHas, milyem22, settings));
      return {
        ...withHas,
        kategori: 'Gram Altın',
        urun: GRAM_22_INVOICE_NAME,
        gram: gram > 0 ? gram : 0,
        adet: gram > 0 ? gram : 1
      };
    }
    case '24gram': {
      const milyem24 = resolveMilyemForInvoiceName(GRAM_24_INVOICE_NAME, getProductCatalog(settings)) || 0.995;
      const gram = gramForProductOverride(withHas, milyem24, settings, () =>
        wholeGramAltinFromAmount(withHas, milyem24, settings));
      return {
        ...withHas,
        kategori: 'Gram Altın',
        urun: GRAM_24_INVOICE_NAME,
        gram: gram > 0 ? gram : 0,
        adet: gram > 0 ? gram : 1
      };
    }
    case 'enYakinUrun': {
      // Tutar çeyrek/yarım/tam fiyatına denk geliyorsa o ürün adet adet kesilir;
      // gram altına denk geliyorsa gram altın. Hiçbiri uymazsa eski davranış.
      const pick = resolveNearestProductForAmount(withHas, settings);
      if (pick) return applyNearestProductToIslem(withHas, pick, settings, 'conditionNearestProduct');
      return applyInvoiceProductTypeOverride(withHas, '24gram', settings);
    }
    default: {
      const tip = String(targetType || '').trim();
      if (tip.startsWith(SARRAFIYE_TYPE_PREFIX)) {
        const ad = tip.slice(SARRAFIYE_TYPE_PREFIX.length);
        const row = getProductCatalog(settings).find(
          (r) => r.category === 'Sarrafiye' && r.productName === ad && r.mode === 'hasFactor'
        );
        if (!row) return withHas;
        const payable = resolveIslemPayableAmount(withHas);
        const islemHas = Number(withHas?.hasFiyati) || 0;
        const liveHas = Number(settings?.liveHasSellPrice) || 0;
        // Fiyat tablosu bugünü gösterir; işlem anına has oranıyla ölçeklenir
        // (resolveNearestProductForAmount ile aynı yaklaşım).
        const guvenilirHas = islemHas > 0 && Math.abs(islemHas - payable) >= 0.01;
        const oran = guvenilirHas && liveHas > 0 ? islemHas / liveHas : 1;
        const olcek = oran >= 0.5 && oran <= 2 ? oran : 1;
        const unitPrice = round2(lookupLiveProductPrice(row.productName, resolveLiveProductPrices(settings)) * olcek);
        const pieces = sarrafiyePiecesForAmount({
          amount: payable,
          row,
          unitPrice,
          hasFiyati: guvenilirHas ? islemHas : liveHas
        });
        return applyNearestProductToIslem(
          withHas,
          { row, pieces, unitPrice, diff: round2(Math.abs(unitPrice * pieces - payable)) },
          settings,
          'productTypeOverride'
        );
      }
      return withHas;
    }
  }
};

/* ------------------------------------------------------------------ *
 * "Tutara en yakın ürün" şartı
 * Ayrıntılı gerekçe: src/utils/invoiceAmountProductRule.js
 * ------------------------------------------------------------------ */

/** İşlemdeki ürünün, kayıtlı adet/grama göre güncel has (altın) değeri. */
export const estimateIslemMarketGoldValue = (islem, settings = null) => {
  const withHas = enrichIslemWithLiveHasPrice(islem, settings?.liveHasSellPrice);
  const hasFiyati = Number(withHas?.hasFiyati) || 0;
  if (!(hasFiyati > 0)) return 0;

  const row = findCatalogRowForIslem(islem, getProductCatalog(settings));
  const adet = Number(islem?.adet) || 0;
  const gram = Number(islem?.gram) || 0;

  if (row?.mode === 'hasFactor' && Number(row.hasFactor) > 0 && adet > 0) {
    return round2(Number(row.hasFactor) * adet * hasFiyati);
  }
  if (row?.mode === 'milyem' && Number(row.milyem) > 0) {
    const unitGrams = parseUnitGramsFromProductName(row.productName);
    const totalGram = gram > 0 ? gram : round2(adet * unitGrams);
    if (totalGram > 0) return round2(totalGram * hasFiyati * Number(row.milyem));
  }
  if (gram > 0) return round2(gram * hasFiyati);
  return 0;
};

/**
 * Şart uygulanmalı mı, uygulanacaksa hangi ürün.
 *
 * Hepsi sağlanmalıdır:
 *  1. Şart açık (ayarlar → Fatura Kuralları → Otomatik şartlar)
 *  2. İşlem bir Satış
 *  3. Faturalanacak tutar, işlem toplamından düşük (kısmen nakit ödenmiş)
 *  4. Kayıtlı ürünün güncel has değeri, faturalanacak tutardan büyük
 *     → yani birim fiyat piyasanın altına düşüyor
 *  5. Güncel Fiyat Ekranı fiyatı tutara tolerans içinde denk gelen bir ürün var
 *  6. Bulunan ürün, mevcut duruma göre tutara daha yakın
 *
 * @returns {{row, pieces, unitPrice, totalPrice, diff, marketGoldValue}|null}
 */
export const resolveAmountNearestProductForIslem = (islem, settings = null) => {
  const rule = mergeAmountNearestProductRule(settings?.amountNearestProductRule);
  if (!rule.enabled) return null;
  if (!isSatisIslem(islem)) return null;

  const payable = resolveIslemPayableAmount(islem);
  if (!(payable > 0)) return null;

  const toplam = Number(islem?.toplamTutar) || 0;
  if (!isPartialBankPaymentIslem(islem)) return null;

  const marketGoldValue = estimateIslemMarketGoldValue(islem, settings);
  if (!(marketGoldValue > payable)) return null;

  const withHas = enrichIslemWithLiveHasPrice(islem, settings?.liveHasSellPrice);
  const islemHas = Number(withHas?.hasFiyati) || 0;
  const liveHas = Number(settings?.liveHasSellPrice) || 0;
  const pick = findNearestPricedProduct({
    amount: payable,
    catalog: getProductCatalog(settings),
    priceTable: resolveLiveProductPrices(settings),
    rule,
    hasFiyati: islemHas,
    // Fiyat tablosu bugünü gösterir; işlem anındaki seviyeye çekeriz.
    priceScale: islemHas > 0 && liveHas > 0 ? islemHas / liveHas : 1
  });
  if (!pick) return null;

  // Mevcut hâlden daha iyi olmayan bir öneriyi uygulamayız.
  if (!(pick.diff < Math.abs(payable - marketGoldValue))) return null;

  // Aynı üründen adet azaltmak her zaman güvenlidir (2 gram satıldı, 1 gramlık
  // kısmı bankadan geldi → 1 gram faturalanır). ÜRÜN DEĞİŞTİRMEK ise yalnızca
  // satışın büyük kısmı bankadan geldiyse ve kaynak ürün sarrafiye/gram altınsa
  // doğru olur; 319.000 TL'lik bileziğin 22.000 TL'lik kaporasını "yarım altın"
  // diye faturalamak olmaz.
  const sameRow = findCatalogRowForIslem(islem, getProductCatalog(settings))?.id === pick.row.id;
  if (!sameRow) {
    const kategori = String(islem?.kategori || '').trim().toLocaleLowerCase('tr-TR');
    const izinli = rule.sourceCategories.map((c) => c.toLocaleLowerCase('tr-TR'));
    if (!izinli.includes(kategori)) return null;
    if (!(payable / toplam >= rule.minPaidRatio)) return null;
  }

  return { ...pick, marketGoldValue, sameRow };
};

const formatTl = (n) =>
  `${Number(n || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} TL`;

/** Personelin taslakta göreceği tek satırlık gerekçe. */
export const AMOUNT_PRODUCT_RULE_NAMES = ['amountNearestProduct', 'conditionNearestProduct'];

export const formatAmountNearestProductNote = (trace) => {
  if (!trace) return '';
  const hedef = `${trace.pieces} adet ${trace.toUrun}`;
  const fiyat = `(güncel fiyat ${formatTl(trace.unitPrice)}, fark ${formatTl(trace.diff)})`;
  if (trace.rule === 'conditionNearestProduct') {
    return `Şart: tutara en yakın ürün — ${hedef} olarak kesildi ${fiyat}.`;
  }
  const from = trace.fromAdet > 0 ? `${trace.fromAdet} adet ${trace.fromUrun}` : trace.fromUrun;
  return `Şart: kısmi banka ödemesi — ${from} yerine ${hedef} olarak kesildi ${fiyat}.`;
};

/**
 * Şart sonucu "Tutara en yakın ürün" için arama.
 *
 * Kısmi ödeme şartından farkı: burada kaynak ürün/ödeme oranı aranmaz —
 * personel bu sonucu şartta bilerek seçmiştir. Tolerans ve en fazla adet
 * ayarları "Kısmi banka ödemesinde ürünü tutara göre seç" kartıyla ortaktır.
 */
export const resolveNearestProductForAmount = (islem, settings = null) => {
  const payable = resolveIslemPayableAmount(islem);
  if (!(payable > 0)) return null;
  const withHas = enrichIslemWithLiveHasPrice(islem, settings?.liveHasSellPrice);
  const islemHas = Number(withHas?.hasFiyati) || 0;
  const liveHas = Number(settings?.liveHasSellPrice) || 0;
  const rule = mergeAmountNearestProductRule(settings?.amountNearestProductRule);

  /**
   * Giriş kayıtlarında hasFiyati alanına bazen has fiyatı değil işlem tutarının
   * kendisi yazılmış (ör. 1001360: tutar 3.500 · hasFiyati 3.500). Böyle bir
   * değerle fiyat tablosunu ölçeklemek rastgele ürün seçtiriyordu; o durumda
   * ölçekleme yapmadan bugünkü fiyatlarla ararız.
   */
  const hasLooksLikeAmount = islemHas > 0 && Math.abs(islemHas - payable) < 0.01;
  const guvenilirHas = islemHas > 0 && !hasLooksLikeAmount;

  return findNearestPricedProduct({
    amount: payable,
    catalog: getProductCatalog(settings),
    priceTable: resolveLiveProductPrices(settings),
    rule: { ...rule, enabled: true },
    hasFiyati: guvenilirHas ? islemHas : 0,
    priceScale: guvenilirHas && liveHas > 0 ? islemHas / liveHas : 1
  });
};

/** Bulunan ürünü fatura satırına uygular. */
export const applyNearestProductToIslem = (islem, pick, settings = null, ruleName = 'amountNearestProduct') => {
  const withHas = enrichIslemWithLiveHasPrice(islem, settings?.liveHasSellPrice);
  const row = pick.row;
  const base = {
    ...withHas,
    invoiceSourceUrun: withHas.urun,
    kategori: row.category,
    urun: row.productName,
    /** Personelin ekranda gerekçeyi görebilmesi için iz */
    invoiceAmountRuleApplied: {
      rule: ruleName,
      fromUrun: String(withHas.urun || ''),
      fromAdet: Number(withHas.adet) || 0,
      toUrun: row.productName,
      pieces: pick.pieces,
      unitPrice: pick.unitPrice,
      diff: pick.diff
    }
  };

  // Karma sonuc (or. 1 tam + 1 yarim): satirlari kalem ureticisi acacak.
  if (Array.isArray(pick.combo) && pick.combo.length > 1) {
    return { ...base, invoiceComboLines: pick.combo.map((c) => ({
      category: c.row.category,
      productName: c.row.productName,
      pieces: c.pieces,
      unitPrice: c.unitPrice,
      totalPrice: c.totalPrice,
      mode: c.row.mode,
      hasFactor: Number(c.row.hasFactor) || 0,
      milyem: Number(c.row.milyem) || 0
    })) };
  }

  if (row.mode === 'hasFactor') {
    return {
      ...base,
      adet: pick.pieces,
      gram: 0,
      invoiceMap: { mode: 'hasFactor', hasFactor: Number(row.hasFactor), pieces: pick.pieces }
    };
  }

  const unitGrams = parseUnitGramsFromProductName(row.productName) || 1;
  const gram = round2(unitGrams * pick.pieces);
  return {
    ...base,
    gram,
    adet: pick.pieces,
    invoiceMap: { mode: 'milyem', milyem: Number(row.milyem) }
  };
};

/** Döviz / giriş / kategori detay tablosu — fatura satırı için işlem normalize eder. */
/**
 * Normalize edilmiş işlemi işaretler.
 *
 * NEDEN: normalizeIslemForInvoice AYNI işlem üzerinde iki kez çalıştırılırsa
 * gramı katlıyordu. "5 Gram 24 Ayar × 1 adet" ilk geçişte gram 5 / adet 5
 * oluyor, ikinci geçişte adet(5) × birim gram(5) = 25 grama çıkıyordu. Has
 * bedeli tutarı aştığı için hesap sessizce yüzdeli yedeğe düşüyor ve birim
 * fiyat piyasanın altında çıkıyordu. Birim gramı 1 olan ürünlerde (1 Gram
 * 24 Ayar) 2×1=2 olduğu için hata tesadüfen görünmüyordu.
 *
 * Çağrı zinciri üç kez normalize ediyordu:
 *   invoiceFirestoreService → buildSpecialMatrahItemsFromIslem → resolveSpecialMatrahSplit
 */
const markIslemNormalized = (islem) =>
  islem && !islem.invoiceNormalized ? { ...islem, invoiceNormalized: true } : islem;

export const normalizeIslemForInvoice = (islem, settings = null) => {
  if (!islem) return islem;

  // Taslakta seçilmiş ürün tipi override'ı — normalize edilmiş işlemde de
  // geçerlidir (personel taslakta ürün tipini sonradan değiştirebilir).
  const override = String(islem?.invoiceProductTypeOverride || settings?.invoiceProductTypeOverride || '').trim();
  if (override && isKnownProductTypeOverride(override, settings)) {
    return markIslemNormalized(applyInvoiceProductTypeOverride(islem, override, settings));
  }

  // Bir kez normalize edilen işlem tekrar normalize edilmez.
  if (islem.invoiceNormalized) return islem;

  return markIslemNormalized(normalizeIslemForInvoiceOnce(islem, settings));
};

/**
 * Yeni şart listesi (settings.invoiceRules) — tanımlıysa ESKİ kural setlerinin
 * (invoiceConditionRules / krediKartiRule / amountNearestProductRule) YERİNE
 * geçer. İki sistemin aynı anda çalışması hangi kuralın uygulandığını
 * anlaşılmaz kılıyordu.
 *
 * @returns {{ rule, sonuc }|null} eşleşen şart, yoksa null
 */
/** İşleme uyan şartlar — liste sırasıyla. */
const matchingInvoiceRules = (islem, settings = null) => {
  const sartlar = mergeInvoiceRules(settings?.invoiceRules);
  if (!sartlar.length || !islem) return [];
  const girdi = ruleInputsFromIslem(islem, {
    odenecek: resolveIslemPayableAmount(islem),
    kismiOdeme: isPartialBankPaymentIslem(islem)
  });
  return sartlar.filter((r) => ruleMatches(r, girdi));
};

export const resolveInvoiceRuleForIslem = (islem, settings = null) => {
  const rule = matchingInvoiceRules(islem, settings)[0];
  return rule ? { rule, sonuc: rule.sonuc } : null;
};

/** Bu işlem için hiç fatura taslağı açılmamalı mı? */
export const shouldSkipInvoiceByRules = (islem, settings = null) =>
  resolveInvoiceRuleForIslem(islem, settings)?.sonuc === 'faturaKesme';

/**
 * Ürünü koruyup gramı faturalanacak tutarla ORANTILI düşürür.
 *
 * Müşteri satışın bir kısmını nakit ödeyince faturaya yalnız bankadan gelen
 * tutar yazılıyor; gram aynı kalınca birim fiyat düşük görünüyordu.
 *
 *   1,76 gram → 10.500 TL  ise
 *   ?         →  3.850 TL  →  1,76 × (3.850 / 10.500) = 0,645 gram
 *
 * Orantı kurmak, piyasa fiyatından gram tahmin etmekten daha doğru: faturadaki
 * birim fiyat satışın gerçek gram fiyatına eşit kalıyor.
 */
export const reduceIslemGramToPayable = (islem, settings = null) => {
  const gram = Number(islem?.gram) || 0;
  const toplam = round2(Number(islem?.toplamTutar) || 0);
  const odenecek = resolveIslemPayableAmount(islem);
  if (!(gram > 0) || !(toplam > 0) || !(odenecek > 0)) return null;
  if (odenecek >= toplam) return null; // tamamı faturalanıyor, düşürmeye gerek yok

  const yeniGram = round2(gram * (odenecek / toplam));
  if (!(yeniGram > 0)) return null;

  return {
    ...islem,
    gram: yeniGram,
    adet: 0,
    invoiceAmountRuleApplied: {
      rule: 'miktarDusur',
      fromGram: gram,
      toGram: yeniGram,
      oran: round2(odenecek / toplam)
    }
  };
};

const normalizeIslemForInvoiceOnce = (islem, settings = null) => {
  // Şart listesi tanımlıysa yalnız o geçerli.
  const eslesenler = matchingInvoiceRules(islem, settings);
  for (const rule of eslesenler) {
    const sonuc = rule.sonuc;
    if (sonuc === 'enYakinUrun') {
      /*
       * resolveAmountNearestProductForIslem KULLANILMAZ: o fonksiyon eski kural
       * setinin kapisidir ve icinde "yalniz kismi banka odemesinde", "yalniz
       * kaynak sarrafiye/gram altinsa" gibi gizli on kosullar tasir. Yeni sart
       * listesinde bu kararlari zaten kullanicinin sectigi kutular (odeme
       * kanali, banka payi, tutar, kategori) veriyor; gizli kosullar sarti
       * sessizce calismaz kiliyordu — kart satislarinda ve tamami havaleli
       * doviz satislarinda "tutara en yakin urun" hic devreye girmiyordu.
       *
       * Fiyati tutari asan aday zaten findNearestPricedProduct icinde eleniyor
       * (has bedeli > faturalanacak tutar), bu yuzden koruma kaybi yok.
       */
      const nearest = resolveNearestProductForAmount(islem, {
        ...settings,
        amountNearestProductRule: {
          enabled: true,
          toleranceTl: rule.toleransTl,
          maxPieces: 99,
          minPaidRatio: 0,
          // Aday urunler fiyat tablosu olan kategorilerden secilir.
          categories: ['Sarrafiye', 'Gram Altın']
        }
      });
      if (nearest) return applyNearestProductToIslem(islem, nearest, settings, 'conditionNearestProduct');
      // Tutara yakin urun bulunamadi: bu sart sonuc uretmedi, ALTTAKI sarta gec.
      continue;
    }
    if (sonuc === 'miktarDusur') {
      // Tartilan urunlerde (taki, hurda) urun dogru, miktar yanlis: musteri
      // bir kismini nakit odeyince faturaya yalniz banka kismi yaziliyor ve
      // gram ayni kalinca birim fiyat piyasanin altina dusuyordu. Urunu
      // degistirmek yerine grami faturalanacak tutara gore hesapliyoruz.
      const azaltilmis = reduceIslemGramToPayable(islem, settings);
      if (azaltilmis) return azaltilmis;
      return islem;
    }
    if (INVOICE_PRODUCT_TYPE_OPTIONS.some((o) => o.value === sonuc)) {
      return applyInvoiceProductTypeOverride(islem, sonuc, settings);
    }
    // 'olduguGibi' / 'faturaKesme': urun degismez, arama burada durur.
    return islem;
  }

  // Sart listesi tanimliysa ve hicbiri sonuc uretmediyse urun oldugu gibi kalir.
  if (mergeInvoiceRules(settings?.invoiceRules).length) return islem;
  return normalizeIslemForInvoiceLegacy(islem, settings);
};

/** Şart listesi boşken çalışan eski davranış. */
const normalizeIslemForInvoiceLegacy = (islem, settings = null) => {
  // Giriş / döviz şartları (tutar): örn. 3000 altı → tamir (ürün döviz olsa bile).
  // DİKKAT: varsayılan iki kural tüm tutar aralığını kapsadığı için Giriş
  // işlemlerinde kategori tablosuna sıra gelmez — bu bilinçli önceliktir.
  if (isAmountConditionEligibleIslem(islem)) {
    const action = resolveAmountBasedInvoiceAction(islem, settings);
    if (action && INVOICE_PRODUCT_TYPE_OPTIONS.some((o) => o.value === action)) {
      return applyInvoiceProductTypeOverride(islem, action, settings);
    }
  }

  // Satış şartları (tutar). Varsayılanda satış kapsamlı kural yoktur; yalnızca
  // kullanıcı tanımlarsa devreye girer, tanımlı değilse kategori tablosuna düşer.
  if (isSatisIslem(islem)) {
    const satisAction = resolveInvoiceConditionAction(settings, 'satis', resolveIslemPayableAmount(islem), {
      partialPayment: isPartialBankPaymentIslem(islem)
    });
    if (satisAction && INVOICE_PRODUCT_TYPE_OPTIONS.some((o) => o.value === satisAction)) {
      return applyInvoiceProductTypeOverride(islem, satisAction, settings);
    }

    // Kısmi banka ödemesi → tutara en yakın ürün
    const nearest = resolveAmountNearestProductForIslem(islem, settings);
    if (nearest) return applyNearestProductToIslem(islem, nearest, settings);
  }

  if (isDovizSaleIslem(islem)) {
    const withHas = enrichIslemWithLiveHasPrice(islem, settings?.liveHasSellPrice);
    const catalog = getProductCatalog(settings);
    // Gram altın tam adet (kullanıcı kararı 02.10.2026)
    const gram = wholeGramAltinFromAmount(
      withHas,
      resolveMilyemForInvoiceName(GRAM_24_INVOICE_NAME, catalog) || 0.995,
      settings
    );
    return {
      ...withHas,
      invoiceSourceUrun: withHas.urun,
      kategori: 'Gram Altın',
      urun: GRAM_24_INVOICE_NAME,
      gram: gram > 0 ? gram : 0,
      adet: gram > 0 ? gram : 1,
      invoiceMap: {
        mode: 'milyem',
        milyem: resolveMilyemForInvoiceName(GRAM_24_INVOICE_NAME, catalog) || 0.995
      }
    };
  }

  if (isGirisIslem(islem)) {
    // Kurallar eşleşmediyse eski varsayılan: 22 ayar takı
    const gram = gramFromAmountFor22AyarTaki(islem, settings);
    return {
      ...islem,
      kategori: 'Takı Ürün',
      urun: TAKI_22_AYAR_INVOICE_NAME,
      gram: gram > 0 ? gram : 0,
      adet: 0,
      invoiceMap: {
        mode: 'milyem',
        milyem: Number(settings?.milyem22Ayar) || MILYEM_22_AYAR_DEFAULT
      }
    };
  }

  // Kategori detay tablosu (Şartlar)
  const catalogRow = findCatalogRowForIslem(islem, getProductCatalog(settings));
  if (catalogRow) {
    return applyCatalogRowToIslem(islem, catalogRow, settings);
  }

  // Gram altın satışlarında ürün adını fatura standardına çek
  if (isGramAltinStyleUrun(islem?.urun, islem?.kategori)) {
    const name = resolveGramAltinInvoiceName(islem?.urun) || GRAM_24_INVOICE_NAME;
    const totalGram = resolveTotalGramsForIslem(islem);
    const catalog = getProductCatalog(settings);
    return {
      ...islem,
      kategori: 'Gram Altın',
      urun: name,
      gram: totalGram > 0 ? totalGram : Number(islem?.gram) || 0,
      adet: totalGram > 0 ? totalGram : Number(islem?.adet) || 1,
      invoiceMap: {
        mode: 'milyem',
        milyem: resolveMilyemForInvoiceName(name, catalog) || (name === GRAM_22_INVOICE_NAME ? 0.916 : 0.995)
      }
    };
  }

  return islem;
};

export const resolveIslemCategoryKey = (islem) => {
  const kategori = String(islem?.kategori || '').trim();
  if (kategori) return kategori;
  return 'default';
};

export const getWorkmanshipPercentFromSettings = (settings, categoryKey) => {
  const rates = settings?.workmanshipRates || {};
  const key = String(categoryKey || '').trim();
  const raw = rates[key] ?? rates.default ?? 10;
  const num = Number(raw);
  if (!Number.isFinite(num) || num < 0) return 10;
  return Math.min(num, 50);
};

export const createEmptyLineItem = () => ({
  id: `item-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  islemId: null,
  islemNo: '',
  productName: '',
  quantity: 1,
  unit: 'ADET',
  unitPrice: 0,
  discountRate: 0,
  discountAmount: 0,
  vatRate: 0,
  vatAmount: 0,
  lineTotal: 0,
  description: '',
  category: '',
  gram: null,
  workmanship: null,
  lineType: 'product',
  ozelMatrahCode: null,
  ozelMatrahAmount: null
});

/** Güncel has satış fiyatı (ALTINTRY sell) işlemde yoksa enriche eder. */
/**
 * İşleme güncel has (altın) fiyatını ekler.
 *
 * DİKKAT: Döviz işlemlerinde `hasFiyati` alanı ALTIN has fiyatını değil, o
 * dövizin kurunu tutuyor (ör. USD için 49,04). Bir döviz işlemi altın ürününe
 * çevrilirken bu değer kullanılınca gram hesabı saçmalıyordu — 4.950 TL'lik
 * USD satışı "109 gram 22 ayar bilezik, gramı 43 TL" olarak faturalanmıştı.
 * Bu yüzden Döviz kategorisinde alandaki değer yok sayılıp canlı has fiyatı
 * kullanılıyor.
 */
export const enrichIslemWithLiveHasPrice = (islem, liveHasSell) => {
  if (!islem) return islem;
  const kayitli = Number(islem.hasFiyati) || 0;
  const live = Number(liveHasSell) || 0;
  if (kayitli > 0) {
    /*
     * Doviz isleminde bu alan bazen DOVIZ KURUNU tutuyor (USD icin 49,04),
     * bazen de gercek altin has fiyatini (EUR isleminde 6.640,75). Kategoriye
     * bakip hepsini atmak, dogru kayitli fiyati da cope atiyordu. Olcut deger
     * in KENDISI: canli has fiyatinin yanina bile yaklasmiyorsa kurdur.
     */
    const kurGibi = live > 0 && kayitli < live * 0.4;
    if (!kurGibi) return islem;
  }
  if (!(live > 0)) return islem;
  return { ...islem, hasFiyati: live };
};

const applyCatalogRowToIslem = (islem, row, settings = null) => {
  const catalog = getProductCatalog(settings);
  const withHas = enrichIslemWithLiveHasPrice(islem, settings?.liveHasSellPrice);
  const adet = Number(withHas.adet) > 0 ? Number(withHas.adet) : 1;
  const unitGrams =
    parseUnitGramsFromProductName(row.productName) || parseUnitGramsFromProductName(withHas.urun);

  if (row.mode === 'invoiceAs' && row.invoiceAs) {
    const totalGram = resolveTotalGramsForIslem(withHas, unitGrams);
    return {
      ...withHas,
      invoiceSourceUrun: withHas.urun,
      kategori: 'Takı Ürün',
      urun: row.invoiceAs,
      gram: totalGram,
      adet: 0,
      invoiceMap: {
        mode: 'milyem',
        milyem: row.milyem || resolveMilyemForInvoiceName(row.invoiceAs, catalog) || 0.333
      }
    };
  }

  if (row.mode === 'hasFactor' && row.hasFactor != null) {
    return {
      ...withHas,
      invoiceSourceUrun: withHas.urun,
      kategori: 'Sarrafiye',
      urun: String(withHas.urun || row.productName).trim() || row.productName,
      adet,
      invoiceMap: { mode: 'hasFactor', hasFactor: Number(row.hasFactor), pieces: adet }
    };
  }

  if (row.mode === 'empty') {
    const grams = resolveTotalGramsForIslem(withHas, unitGrams);
    if (isWholeNumberGrams(grams)) {
      return {
        ...withHas,
        invoiceSourceUrun: withHas.urun,
        kategori: 'Gram Altın',
        urun: GRAM_24_INVOICE_NAME,
        gram: round2(grams),
        adet: round2(grams),
        invoiceMap: {
          mode: 'milyem',
          milyem: resolveMilyemForInvoiceName(GRAM_24_INVOICE_NAME, catalog) || 0.995
        }
      };
    }
    return {
      ...withHas,
      invoiceSourceUrun: withHas.urun,
      kategori: 'Takı Ürün',
      urun: BILEZIK_22_INVOICE_NAME,
      gram: round2(grams),
      adet: 0,
      invoiceMap: {
        mode: 'milyem',
        milyem: resolveMilyemForInvoiceName(BILEZIK_22_INVOICE_NAME, catalog) || 0.916
      }
    };
  }

  if (row.mode === 'milyem' && row.milyem != null) {
    if (row.category === 'Gram Altın' || isGramAltinStyleUrun(row.productName, row.category)) {
      const totalGram = resolveTotalGramsForIslem(withHas, unitGrams);
      const name =
        resolveGramAltinInvoiceName(row.productName) ||
        resolveGramAltinInvoiceName(withHas.urun) ||
        GRAM_24_INVOICE_NAME;
      return {
        ...withHas,
        invoiceSourceUrun: withHas.urun,
        kategori: 'Gram Altın',
        urun: name,
        gram: totalGram,
        adet: totalGram,
        invoiceMap: { mode: 'milyem', milyem: Number(row.milyem) }
      };
    }
    const grams = Number(withHas.gram) > 0 ? round2(Number(withHas.gram)) : 0;
    return {
      ...withHas,
      invoiceSourceUrun: withHas.urun,
      kategori: 'Takı Ürün',
      urun: String(withHas.urun || row.productName).trim() || row.productName,
      gram: grams,
      invoiceMap: { mode: 'milyem', milyem: Number(row.milyem) }
    };
  }

  return withHas;
};

/**
 * İşlemden özel matrah bölüşümü.
 * @returns {{ totalPayable: number, goldAmount: number, workmanshipNet: number, workmanshipVat: number, ozelMatrahCode: string, warning: string }}
 */
/**
 * Ürünü değiştirilen faturada ALTINA düşen pay (0-1).
 *
 * Gram, tutarın tamamı yerine bu payı üzerinden hesaplanır; böylece geri
 * kalanı işçilik olur ve KDV kuruşlarda kalmaz.
 */
/*
 * GRAM BASINA ISCILIK (kullanici karari 07.10.2026). Personel iscilige "gram
 * basina TL" olarak bakiyor; tutarin yuzdesi gram fiyatiyla oynuyordu
 * (bilezikte %1 → ~50 TL/gr, 14 ayarda %10 → ~350 TL/gr). Tanimliysa yuzde
 * yerine bu kullanilir: gram = tutar / (has × milyem + iscilik × 1,20).
 * Kalan (kurus) yine iscilik satirina gider. KDV HARIC, TL/gr.
 * Ayarlar: settings.gramBasiIscilikNet = { '22bilezik': 175, ... }; 0 → yuzde.
 */
export const GRAM_BASI_ISCILIK_NET_DEFAULT = { '22bilezik': 175, '22taki': 175, '14taki': 250 };

export const gramBasiIscilikNet = (targetType, settings) => {
  const tablo = { ...GRAM_BASI_ISCILIK_NET_DEFAULT, ...(settings?.gramBasiIscilikNet || {}) };
  const v = Number(tablo[targetType]);
  return Number.isFinite(v) && v > 0 ? v : 0;
};

/** Gram basina iscilikle gram: tutar / (has × milyem + iscilikNet × 1,20). */
export const gramFromAmountWithPerGramWorkmanship = (islem, milyem, iscilikNet, settings = null) => {
  const amount = resolveIslemPayableAmount(islem);
  const has = Number(enrichIslemWithLiveHasPrice(islem, settings?.liveHasSellPrice)?.hasFiyati) || 0;
  /*
   * HAS FİYATI YOKSA GRAM HESAPLANMAZ.
   *
   * Eskiden has 0 olsa bile işçilik payı birimi sıfırdan büyük tutuyor ve
   * tutar yalnız işçiliğe bölünüyordu: bakiye düzeltme kaydında (hasFiyati 0)
   * 50.000 TL'lik fatura "238,1 gram × 199,5 TL" çıktı — 22 ayar bilezik gram
   * fiyatı ~6.000 TL olmalıydı (27.10.2026 personel geri dönüşü, SERPİL ÇAMCI).
   *
   * 0 dönünce ürün tipi şartı gramı değiştirmez; taslak eksik kalır ve
   * personelin gözüne çarpar — sessizce yanlış fatura üretmekten iyidir.
   */
  if (!(has > 0)) return 0;
  const birim = has * (Number(milyem) || 0) + Number(iscilikNet || 0) * 1.2;
  if (!(amount > 0) || !(birim > 0)) return 0;
  return round2(amount / birim);
};

const altinPayiForOverride = (targetType, settings) => {
  const tablo = settings?.cevrilenUrunIscilikYuzdesi || CEVRILEN_ISCILIK_YUZDESI_DEFAULT;
  const yuzde = Number(tablo?.[targetType]);
  if (!Number.isFinite(yuzde) || yuzde <= 0 || yuzde >= 100) return 1;
  return 1 - yuzde / 100;
};

/** Altın Tamir Ücreti faturasında işçilik satırının birim fiyatı (KDV hariç). */
export const resolveTamirIscilikNet = (settings) => {
  const v = Number(settings?.tamirIscilikNetTl);
  return Number.isFinite(v) && v >= 0 ? v : TAMIR_ISCILIK_NET_DEFAULT;
};

export const resolveSpecialMatrahSplit = (islem = {}, settings = null) => {
  const withHas = enrichIslemWithLiveHasPrice(islem, settings?.liveHasSellPrice);
  const normalized = normalizeIslemForInvoice(withHas, settings);
  const totalPayable = resolveIslemPayableAmount(normalized);
  const isServiceLine = String(normalized?.urun || '') === ALTIN_TAMIR_UCRETI_NAME;

  const gram = Number(normalized?.gram) || 0;
  const hasFiyati = Number(normalized?.hasFiyati) || 0;
  const hasEsdeger = Number(normalized?.hasEsdeger) || 0;
  const map = normalized?.invoiceMap || null;

  let goldAmount = 0;
  if (map?.mode === 'hasFactor' && Number(map.hasFactor) > 0 && hasFiyati > 0) {
    const pieces = Number(map.pieces) > 0 ? Number(map.pieces) : Number(normalized?.adet) || 1;
    goldAmount = round2(Number(map.hasFactor) * pieces * hasFiyati);
  } else if (map?.mode === 'milyem' && Number(map.milyem) > 0 && gram > 0 && hasFiyati > 0) {
    goldAmount = round2(gram * hasFiyati * Number(map.milyem));
  } else if (gram > 0 && hasFiyati > 0) {
    const urun = String(normalized?.urun || '');
    const catalog = getProductCatalog(settings);
    const mappedMilyem = resolveMilyemForInvoiceName(urun, catalog);
    if (mappedMilyem != null) {
      goldAmount = round2(gram * hasFiyati * mappedMilyem);
    } else if (urun.includes('22 Ayar')) {
      const milyem = Number(settings?.milyem22Ayar) || MILYEM_22_AYAR_DEFAULT;
      goldAmount = round2(gram * hasFiyati * milyem);
    } else {
      goldAmount = round2(gram * hasFiyati);
    }
  } else if (hasEsdeger > 0 && hasFiyati > 0) {
    goldAmount = round2(hasEsdeger * hasFiyati);
  }

  const workmanshipPercent = getWorkmanshipPercentFromSettings(settings, resolveIslemCategoryKey(normalized));

  let warning = '';
  if (!(totalPayable > 0)) {
    warning = 'İşlem tutarı yok; özel matrah hesaplanamadı.';
    return {
      totalPayable: 0,
      goldAmount: 0,
      workmanshipNet: 0,
      workmanshipVat: 0,
      ozelMatrahCode: OZEL_MATRAH_CODE_DEFAULT,
      warning
    };
  }

  const applySettingsWorkmanship = () => {
    const workmanshipGross = round2(totalPayable * (workmanshipPercent / 100));
    const workmanshipNet = round2(workmanshipGross / 1.2);
    const workmanshipVat = round2(workmanshipGross - workmanshipNet);
    const gold = round2(Math.max(0, totalPayable - workmanshipGross));
    return { goldAmount: gold, workmanshipNet, workmanshipVat };
  };

  /*
   * Tamir ücreti bir HİZMET: has hesabı yapılmaz. İşçilik oranla değil SABİT
   * yazılır — her tamir faturasında işçilik satırının birim fiyatı aynıdır
   * (varsayılan 100 TL, KDV hariç). Üstü ürün satırına gider.
   *
   * Tutar sabiti karşılayamayacak kadar küçükse (ör. 90 TL'lik tamir) ürün
   * satırı eksiye düşmesin diye eski oransal bölüşüme dönülür.
   */
  if (isServiceLine) {
    const sabitNet = resolveTamirIscilikNet(settings);
    const sabitBrut = round2(sabitNet * 1.2);
    if (sabitNet > 0 && sabitBrut < totalPayable) {
      return {
        totalPayable,
        goldAmount: round2(totalPayable - sabitBrut),
        workmanshipNet: sabitNet,
        workmanshipVat: round2(sabitBrut - sabitNet),
        ozelMatrahCode: OZEL_MATRAH_CODE_DEFAULT,
        warning: ''
      };
    }
    const split = applySettingsWorkmanship();
    return {
      totalPayable,
      goldAmount: split.goldAmount,
      workmanshipNet: split.workmanshipNet,
      workmanshipVat: split.workmanshipVat,
      ozelMatrahCode: OZEL_MATRAH_CODE_DEFAULT,
      warning: ''
    };
  }

  if (!(goldAmount > 0)) {
    const split = applySettingsWorkmanship();
    if (split.workmanshipNet <= 0) {
      warning = 'Has bedeli hesaplanamadı (gram/has fiyatı eksik). İşçilik oranı uygulanamadı.';
      return {
        totalPayable,
        goldAmount: totalPayable,
        workmanshipNet: 0,
        workmanshipVat: 0,
        ozelMatrahCode: OZEL_MATRAH_CODE_DEFAULT,
        warning
      };
    }
    return {
      totalPayable,
      goldAmount: split.goldAmount,
      workmanshipNet: split.workmanshipNet,
      workmanshipVat: split.workmanshipVat,
      ozelMatrahCode: OZEL_MATRAH_CODE_DEFAULT,
      warning: ''
    };
  }

  if (goldAmount >= totalPayable) {
    const split = applySettingsWorkmanship();
    if (split.workmanshipNet > 0) {
      return {
        totalPayable,
        goldAmount: split.goldAmount,
        workmanshipNet: split.workmanshipNet,
        workmanshipVat: split.workmanshipVat,
        ozelMatrahCode: OZEL_MATRAH_CODE_DEFAULT,
        warning: ''
      };
    }
    return {
      totalPayable,
      goldAmount: totalPayable,
      workmanshipNet: 0,
      workmanshipVat: 0,
      ozelMatrahCode: OZEL_MATRAH_CODE_DEFAULT,
      warning: ''
    };
  }

  const workmanshipGross = round2(totalPayable - goldAmount);
  const workmanshipNet = round2(workmanshipGross / 1.2);
  const workmanshipVat = round2(workmanshipGross - workmanshipNet);

  return {
    totalPayable,
    goldAmount: round2(goldAmount),
    workmanshipNet,
    workmanshipVat,
    ozelMatrahCode: OZEL_MATRAH_CODE_DEFAULT,
    warning
  };
};

export const calculateLineItem = (item = {}) => {
  const quantity = Number(item.quantity) || 0;
  const unitPrice = Number(item.unitPrice) || 0;
  const discountRate = Number(item.discountRate) || 0;
  const gross = quantity * unitPrice;
  const discountAmount =
    item.discountAmount != null && item.discountAmount !== ''
      ? Number(item.discountAmount) || 0
      : round2((gross * discountRate) / 100);
  const lineNet = Math.max(0, round2(gross - discountAmount));
  const vatRate = Number(item.vatRate) || 0;
  const vatAmount = round2((lineNet * vatRate) / 100);
  const computedTotal = round2(lineNet + vatAmount);

  /**
   * Kuruş artığı. Birim fiyat × miktar kuruşa yuvarlandığı için toplam, tahsil
   * edilen tutarı birkaç kuruş kaçırabiliyor. Artık YALNIZCA KDV %0 olan has
   * satırına yazılır; hiçbir vergi rakamı değişmez.
   * Kullanıcı birim fiyatı elle değiştirirse fark büyüyeceği için devre dışı
   * kalır — bayat bir artık, düzenlemeyi ezemez.
   */
  const overrideRaw = item.lineAmountOverride;
  const override =
    overrideRaw != null && overrideRaw !== '' && Number.isFinite(Number(overrideRaw))
      ? round2(Number(overrideRaw))
      : null;
  const overrideUsable =
    override != null && vatRate === 0 && Math.abs(override - computedTotal) <= lineRoundingTolerance(quantity);
  const lineTotal = overrideUsable ? override : computedTotal;

  return {
    ...item,
    quantity,
    unitPrice: round2(unitPrice),
    discountRate,
    discountAmount: round2(discountAmount),
    vatRate,
    vatAmount,
    lineTotal,
    amount: lineTotal,
    lineAmountOverride: overrideUsable ? override : null,
    lineType: item.lineType || 'product',
    ozelMatrahCode: item.ozelMatrahCode || null,
    ozelMatrahAmount: item.ozelMatrahAmount != null ? round2(Number(item.ozelMatrahAmount) || 0) : null,
    matrahWarning: item.matrahWarning || null,
    /** Otomatik şart uygulandıysa personelin göreceği gerekçe */
    autoRuleNote: item.autoRuleNote || null
  };
};

export const calculateInvoiceTotals = (items = []) => {
  const lines = (items || []).map(calculateLineItem);
  const sumVat = (rate) =>
    round2(lines.filter((l) => Number(l.vatRate) === rate).reduce((s, l) => s + Number(l.vatAmount || 0), 0));

  // Her satir once kurusa yuvarlanip sonra toplaniyor: faturada basilan tutarlar
  // satir bazinda yuvarlanmis oluyor, ham carpimlari toplayip sonunda yuvarlamak
  // ozet kutuda 1 kurusluk sapma yapiyordu (ornek: 8.020,03 yazarken fatura
  // 8.020,02 basiyor ve ozetteki toplam Odenecek ile tutmuyordu).
  const goodsServiceAmount = round2(
    lines.reduce((s, l) => {
      const base =
        l.lineAmountOverride != null
          ? round2(Number(l.lineAmountOverride) + Number(l.discountAmount || 0))
          : round2(Number(l.quantity || 0) * Number(l.unitPrice || 0));
      return s + base;
    }, 0)
  );
  const totalDiscount = round2(lines.reduce((s, l) => s + Number(l.discountAmount || 0), 0));
  const taxExclusiveAmount = round2(lines.reduce((s, l) => s + (Number(l.lineTotal || 0) - Number(l.vatAmount || 0)), 0));
  const taxInclusiveAmount = round2(lines.reduce((s, l) => s + Number(l.lineTotal || 0), 0));
  // Özet kutudaki “Özel Matrah 805” = KDV’nin hesaplandığı işçilik neti (tek kez).
  // Ürün satırındaki ozelMatrahAmount (UBL/satır bilgisi) buraya eklenmez; yoksa 2 katına çıkar.
  const ozelMatrahAmount = round2(
    lines
      .filter((l) => l.lineType === 'workmanship')
      .reduce((s, l) => s + (Number(l.lineTotal || 0) - Number(l.vatAmount || 0)), 0)
  );

  return {
    goodsServiceAmount,
    totalDiscount,
    vat0Amount: sumVat(0),
    vat1Amount: sumVat(1),
    vat10Amount: sumVat(10),
    vat20Amount: sumVat(20),
    taxExclusiveAmount,
    taxInclusiveAmount,
    payableAmount: taxInclusiveAmount,
    ozelMatrahCode: OZEL_MATRAH_CODE_DEFAULT,
    ozelMatrahAmount
  };
};

export const normalizeVknTcknFromCustomer = (customerRecord, draft = {}) => {
  const existingVkn = String(draft?.vknTckn || '').trim();
  const existingTc = String(draft?.customerTc || '').trim();
  if (existingVkn) {
    return syncLegacyCustomerFields({
      ...draft,
      vknTckn: existingVkn,
      customerTc: existingTc || existingVkn
    });
  }

  const tc = String(customerRecord?.tcKimlikNo || '').trim();
  if (!tc) return syncLegacyCustomerFields(draft);

  return syncLegacyCustomerFields({
    ...draft,
    vknTckn: tc,
    customerTc: tc
  });
};

/**
 * Kuruş mutabakatı.
 *
 * Birim fiyatlar kuruşa yuvarlandığı için adet > 1 olan faturalarda toplam,
 * tahsil edilen tutarı birkaç kuruş kaçırıyordu:
 *   10 adet Cumhuriyet Ata · 447.000,00 TL → faturada 446.999,92 TL (8 kr eksik)
 *
 * Çözüm: işçilik birim fiyatını ideal değerin çevresinde (en yakından başlayarak)
 * arayıp, hem KDV'nin hem de has bedelinin kuruşu kuruşuna tuttuğu değeri seçeriz.
 * İşçilik zaten "tutardan has bedelinin artanı" olduğu için birkaç kuruşluk
 * kayması sorun değil; toplamın tutması şarttır.
 *
 * Uygun değer bulunamazsa veya işçilik yoksa eski davranışa düşeriz.
 */
export const RECONCILE_MAX_STEPS = 50;

export const reconcileMatrahUnitPrices = ({ totalPayable, goldAmount, workmanshipNet, qty }) => {
  const n = Number(qty) > 0 ? Number(qty) : 1;
  const payable = round2(totalPayable);
  const baseNet = round2(workmanshipNet);
  const fallback = {
    goldUnitPrice: round2(round2(goldAmount) / n),
    workUnitPrice: round2(baseNet / n),
    workmanshipNet: baseNet,
    exact: false
  };
  // İşçilik yoksa uydurmayız.
  if (!(payable > 0) || !(baseNet > 0)) return fallback;

  const idealWorkUnit = baseNet / n;
  const maxDrift = Math.max(1, round2(baseNet * 0.02));

  const tryUnit = (unit) => {
    const u = round2(unit);
    if (!(u > 0)) return null;
    const net = round2(u * n);
    if (Math.abs(net - baseNet) > maxDrift) return null;
    const vat = round2((net * 20) / 100);
    const rest = round2(payable - round2(net + vat));
    if (!(rest > 0)) return null;
    const goldUnit = round2(rest / n);
    // Birim × adet, kalan tutarı kuruşu kuruşuna vermeli.
    if (Math.abs(round2(goldUnit * n) - rest) > 0.004) return null;
    return { goldUnitPrice: goldUnit, workUnitPrice: u, workmanshipNet: net, exact: true };
  };

  for (let step = 0; step <= RECONCILE_MAX_STEPS; step += 1) {
    const candidates =
      step === 0 ? [idealWorkUnit] : [idealWorkUnit - step / 100, idealWorkUnit + step / 100];
    for (let i = 0; i < candidates.length; i += 1) {
      const hit = tryUnit(candidates[i]);
      if (hit) return hit;
    }
  }
  return fallback;
};

/**
 * Tartilan urunde (taki, hurda) gram bos kalmissa tutardan tahmin eder.
 *
 * NEDEN: bazi satislarda gram girilmemis oluyor (or. 40.500 TL'lik 14 ayar
 * taki satisi, gram alani 0). getIslemQuantity boyle bir kayitta 1'e dusuyor
 * ve fatura "1 gram @38.475 TL" olarak cikiyordu. Urunun ayarindan (milyem)
 * ve gunun has fiyatindan gram hesaplamak tek dogru tahmin.
 */
const ensureWeighedItemGram = (islem, settings = null) => {
  const kategori = String(islem?.kategori || '').trim();
  if (kategori !== 'Takı Ürün' && kategori !== 'Hurda') return islem;
  if (Number(islem?.gram) > 0) return islem;

  const withHas = enrichIslemWithLiveHasPrice(islem, settings?.liveHasSellPrice);
  const catalog = getProductCatalog(settings);
  const milyem =
    Number(islem?.invoiceMap?.milyem) ||
    Number(findCatalogRowForIslem(islem, catalog)?.milyem) ||
    Number(resolveMilyemForInvoiceName(String(islem?.urun || ''), catalog)) ||
    0;
  if (!(milyem > 0)) return islem;

  const gram = gramFromAmountForMilyem(withHas, milyem);
  if (!(gram > 0)) return islem;
  return { ...withHas, gram, adet: 0 };
};

export const buildSpecialMatrahItemsFromIslem = (islem, index = 0, settings = null) => {
  const normalized = ensureWeighedItemGram(normalizeIslemForInvoice(islem, settings), settings);

  // Karma sonuc: her urun kendi tutariyla ayri satir olur.
  const combo = normalized?.invoiceComboLines;
  if (Array.isArray(combo) && combo.length > 1) {
    /*
     * Fatura toplami tahsil edilen tutara KURUSU KURUSUNA esit olmali. Karma
     * sonuçta satirlar katalog fiyatiyla yaziliyordu; iki urunun toplami
     * tolerans kadar (en cok 200 TL) sapinca fatura banka/POS tutarindan
     * farkli cikiyor ve mutabakat tutmuyordu. Tutari satirlarin katalog
     * fiyatlari oraninda dagitip artan kurusu son satira veriyoruz.
     */
    const odenecek = resolveIslemPayableAmount(normalized);
    const katalogToplam = round2(combo.reduce((t, c) => t + (Number(c.totalPrice) || 0), 0));
    let kalan = odenecek;
    const paylar = combo.map((c, i) => {
      if (i === combo.length - 1) return round2(kalan);
      const pay = katalogToplam > 0 ? round2((odenecek * (Number(c.totalPrice) || 0)) / katalogToplam) : 0;
      kalan = round2(kalan - pay);
      return pay;
    });

    return combo.flatMap((line, i) =>
      buildSpecialMatrahItemsFromIslem(
        {
          ...normalized,
          invoiceComboLines: null,
          invoiceNormalized: true,
          kategori: line.category,
          urun: line.productName,
          adet: line.mode === 'hasFactor' ? line.pieces : 0,
          gram: line.mode === 'hasFactor' ? 0 : round2(parseUnitGramsFromProductName(line.productName) * line.pieces),
          invoiceMap:
            line.mode === 'hasFactor'
              ? { mode: 'hasFactor', hasFactor: line.hasFactor, pieces: line.pieces }
              : { mode: 'milyem', milyem: line.milyem },
          // Bu satirin faturalanacak tutari (odenen tutardan pay)
          toplamTutar: paylar[i],
          havaleEft: paylar[i],
          nakit: 0,
          krediKarti: 0
        },
        index * 10 + i,
        settings
      )
    );
  }

  const productName = getIslemProductName(normalized);
  const quantity = getIslemQuantity(normalized);
  const unit = getIslemUnit(normalized);
  const baseId = normalized?.id || islem?.id || `idx-${index}`;
  const split = resolveSpecialMatrahSplit(normalized, settings);
  const qty = quantity > 0 ? quantity : 1;

  // Toplam, tahsil edilen tutarı kuruşu kuruşuna tutmalı (bkz. reconcileMatrahUnitPrices)
  const reconciled = reconcileMatrahUnitPrices({
    totalPayable: split.totalPayable,
    goldAmount: split.goldAmount,
    workmanshipNet: split.workmanshipNet,
    qty
  });
  const goldUnitPrice = reconciled.goldUnitPrice;
  const workUnitPrice = reconciled.workUnitPrice;
  const ozelMatrahAmount = reconciled.workmanshipNet;

  // Mutabakat tam tutmadıysa (ör. ondalıklı gram miktarı) kalan kuruş, KDV %0
  // olan has satırına yazılır.
  const workLineNet = round2(workUnitPrice * qty);
  const workLineGross = round2(workLineNet + round2((workLineNet * 20) / 100));
  const goldTarget = round2(split.totalPayable - workLineGross);
  const goldComputed = round2(goldUnitPrice * qty);
  const goldResidue = round2(goldTarget - goldComputed);
  const goldLineAmountOverride =
    goldResidue !== 0 && Math.abs(goldResidue) <= lineRoundingTolerance(qty) ? goldTarget : null;

  const productLine = calculateLineItem({
    id: `item-${baseId}-product`,
    islemId: normalized?.id || islem?.id || null,
    islemNo: normalized?.islemNo || islem?.islemNo || normalized?.id || '',
    productName,
    quantity: qty,
    unit,
    unitPrice: goldUnitPrice,
    discountRate: 0,
    vatRate: 0,
    description: 'Has / ürün bedeli (KDV %0)',
    lineType: 'product',
    category: normalized?.kategori || islem?.kategori || '',
    gram: Number(normalized?.gram) || null,
    workmanship: ozelMatrahAmount || null,
    ozelMatrahCode: split.ozelMatrahCode,
    ozelMatrahAmount,
    lineAmountOverride: goldLineAmountOverride
  });

  const workmanshipLine = calculateLineItem({
    id: `item-${baseId}-workmanship`,
    islemId: normalized?.id || islem?.id || null,
    islemNo: normalized?.islemNo || islem?.islemNo || normalized?.id || '',
    productName: `${productName} İşçiliği`,
    quantity: qty,
    unit,
    unitPrice: workUnitPrice,
    discountRate: 0,
    vatRate: 20,
    description: 'İşçilik (özel matrah / KDV %20)',
    lineType: 'workmanship',
    category: normalized?.kategori || islem?.kategori || '',
    gram: Number(normalized?.gram) || null,
    workmanship: ozelMatrahAmount,
    ozelMatrahCode: split.ozelMatrahCode,
    ozelMatrahAmount
  });

  if (split.warning) {
    productLine.matrahWarning = split.warning;
  }

  const ruleTrace = normalized?.invoiceAmountRuleApplied;
  if (AMOUNT_PRODUCT_RULE_NAMES.includes(ruleTrace?.rule)) {
    productLine.autoRuleNote = formatAmountNearestProductNote(ruleTrace);
  }

  return [productLine, workmanshipLine];
};

export const buildSpecialMatrahItemsFromIslemler = (islemler = [], settings = null) =>
  (islemler || []).flatMap((islem, index) => buildSpecialMatrahItemsFromIslem(islem, index, settings));

export const shouldAutoGenerateItems = (draft = {}) => {
  if (draft?.manuallyEditedItems) return false;
  if (Array.isArray(draft?.items) && draft.items.length > 0) {
    /*
     * ESKIDEN: autoGeneratedItems === true ise kalemler HER ACILISTA yeniden
     * uretiliyordu. Altin fiyati her an oynadigi icin ayni taslak her
     * yenilemede baska gram, baska iscilik ve baska KDV gosteriyordu
     * (8.400 TL'lik bir faturada KDV 70,02 -> 1,50 TL). Kaydedilen ile
     * ekranda gorunen tutmuyordu.
     *
     * Kalemler bir kez uretildikten sonra SABIT kalir; degistirmek icin
     * "Urun Tipi" dugmesi ya da toplu yeniden hesaplama kullanilir.
     * Yalnizca eski bicimli (isciliksiz / eski urun adli) taslaklar
     * yenilenir.
     */
    if (draft.invoiceType === 'ozelMatrah') {
      const hasWorkmanship = draft.items.some((item) => item.lineType === 'workmanship');
      const hasLegacyGramName = draft.items.some((item) => {
        const name = String(item.productName || '');
        return (
          /\d+([.,]\d+)?\s*gram\s*(22|24)\s*ayar/i.test(name) ||
          name.includes('1 Gram 24 Ayar') ||
          name.includes('1 Gram 22 Ayar')
        );
      });
      if (!hasWorkmanship || hasLegacyGramName) return true;
    }
    return false;
  }
  return true;
};

export const buildItemFromIslem = (islem, index = 0) => {
  const quantity = getIslemQuantity(islem);
  const amount = Number(islem.havaleEft) || 0;
  const unitPrice = quantity > 0 ? amount / quantity : amount;
  return calculateLineItem({
    id: `item-${islem?.id || index}`,
    islemId: islem?.id || null,
    islemNo: islem?.islemNo || islem?.id || '',
    productName: getIslemProductName(islem),
    quantity,
    unit: getIslemUnit(islem),
    unitPrice: round2(unitPrice),
    discountRate: 0,
    discountAmount: 0,
    vatRate: 0,
    description: '',
    category: islem?.kategori || '',
    gram: Number(islem?.gram) || null,
    workmanship: Number(islem?.iscilik) || null
  });
};

export const buildItemFromLegacyDraft = (draft) => {
  const quantity = Number(draft?.quantity) || 1;
  const amount = Number(draft?.amount) || 0;
  const unitPrice = quantity > 0 ? amount / quantity : amount;
  return calculateLineItem({
    id: 'item-legacy-1',
    islemId: null,
    islemNo: '',
    productName: draft?.productName || '',
    quantity,
    unit: 'ADET',
    unitPrice: round2(unitPrice),
    discountRate: 0,
    vatRate: 0,
    description: '',
    category: '',
    gram: null,
    workmanship: null
  });
};

const enrichLegacyItem = (item, index) => {
  const quantity = Number(item.quantity) || 0;
  const legacyAmount = Number(item.amount) || Number(item.lineTotal) || 0;
  const unitPrice =
    Number(item.unitPrice) > 0
      ? Number(item.unitPrice)
      : quantity > 0
        ? legacyAmount / quantity
        : legacyAmount;

  return calculateLineItem({
    id: item.id || `item-${item.islemId || index}`,
    islemId: item.islemId || null,
    islemNo: item.islemNo || '',
    productName: item.productName || '',
    quantity: quantity || 1,
    unit: item.unit || 'ADET',
    unitPrice,
    discountRate: item.discountRate ?? 0,
    discountAmount: item.discountAmount,
    vatRate: item.vatRate ?? 0,
    description: item.description || '',
    category: item.category || '',
    gram: item.gram ?? null,
    workmanship: item.workmanship ?? null,
    lineType: item.lineType || 'product',
    ozelMatrahCode: item.ozelMatrahCode || null,
    ozelMatrahAmount: item.ozelMatrahAmount ?? null,
    matrahWarning: item.matrahWarning || null,
    autoRuleNote: item.autoRuleNote || null,
    lineAmountOverride: item.lineAmountOverride ?? null
  });
};

const mapMatrahToInvoiceType = (matrahType) => {
  const key = String(matrahType || '').toLocaleLowerCase('tr-TR');
  if (key.includes('özel') || key.includes('ozel')) return 'ozelMatrah';
  if (key.includes('ticari')) return 'ticari';
  if (key.includes('istisna')) return 'istisna';
  return 'satis';
};

const mapInvoiceTypeToMatrah = (invoiceType) => {
  const map = {
    ozelMatrah: 'Özel Matrah',
    satis: 'Standart',
    ticari: 'Ticari',
    istisna: 'İstisna'
  };
  return map[invoiceType] || 'Özel Matrah';
};

const resolveVknTckn = (draft) =>
  String(draft?.vknTckn || draft?.customerTc || draft?.customerTaxNo || '').trim();

/*
 * Form her tusta normalizeInvoiceDraft'tan geciyor. Metinler burada TAM
 * kirpilinca kelime sonundaki bosluk aninda siliniyor, "KARTAL VERGI" yazilamiyor,
 * "KARTALVERGI" oluyordu (06.10.2026). Yazarken yalniz bastaki bosluk atilir;
 * tam kirpma kayitta (trimDraftTextFields).
 */
const basBoslukSil = (v) => String(v ?? '').replace(/^\s+/, '');

const syncLegacyCustomerFields = (draft) => {
  const vknTckn = resolveVknTckn(draft);
  const city = basBoslukSil(draft?.city || draft?.customerIl || '');
  const district = basBoslukSil(draft?.district || draft?.customerIlce || '');
  const address = basBoslukSil(draft?.address || draft?.customerAddress || '');
  const taxOffice = basBoslukSil(draft?.taxOffice || draft?.customerTaxOffice || '');
  const customerName = basBoslukSil(draft?.customerName || draft?.title || '');
  const digits = vknTckn.replace(/\D/g, '');
  const customerType = draft?.customerType || 'sahis';
  const isFirma = customerType === 'firma';

  return {
    ...draft,
    customerName,
    title: basBoslukSil(draft?.title || customerName),
    vknTckn,
    customerTc: isFirma ? draft?.customerTc || null : digits || draft?.customerTc || null,
    customerTaxNo: isFirma ? digits || draft?.customerTaxNo || null : draft?.customerTaxNo || null,
    city,
    district,
    address,
    taxOffice,
    customerIl: city,
    customerIlce: district,
    customerAddress: address,
    customerTaxOffice: taxOffice
  };
};

export const ensureDraftItems = (draft) => {
  if (Array.isArray(draft?.items) && draft.items.length > 0) {
    return draft.items.map(enrichLegacyItem);
  }
  if (draft?.productName || Number(draft?.amount) > 0) {
    return [buildItemFromLegacyDraft(draft)];
  }
  return [createEmptyLineItem()];
};

export const normalizeInvoiceDraft = (draft = {}, settings = null) => {
  const settingsDraft = settings
    ? {
        paymentType: settings.defaultPaymentType || undefined,
        currency: settings.defaultCurrency || undefined,
        matrahType: settings.defaultMatrahType || undefined,
        invoiceType: settings.defaultMatrahType ? mapMatrahToInvoiceType(settings.defaultMatrahType) : undefined
      }
    : {};

  const invoiceDateRaw = draft.invoiceDate;
  let invoiceDate = invoiceDateRaw;
  let invoiceTime = draft.invoiceTime || DEFAULT_PROFESSIONAL_DRAFT.invoiceTime;
  if (invoiceDateRaw && typeof invoiceDateRaw === 'string' && invoiceDateRaw.includes('T')) {
    const d = new Date(invoiceDateRaw);
    if (!Number.isNaN(d.getTime())) {
      // UTC'ye düşürmek gece 00:00–03:00 arası işlemleri bir gün geriye atıyordu.
      invoiceDate = getIstanbulDayKey(d);
      if (!draft.invoiceTime) {
        invoiceTime = getIstanbulTimePart(d);
      }
    }
  }

  let merged = syncLegacyCustomerFields({
    ...DEFAULT_PROFESSIONAL_DRAFT,
    ...settingsDraft,
    ...draft,
    invoiceDate: invoiceDate || getIstanbulDayKey(new Date()),
    invoiceTime,
    invoiceNote: String(draft?.invoiceNote ?? DEFAULT_INVOICE_NOTE),
    invoiceType: draft?.invoiceType || mapMatrahToInvoiceType(draft?.matrahType || settingsDraft.matrahType),
    matrahType: draft?.matrahType || mapInvoiceTypeToMatrah(draft?.invoiceType),
    currency: draft?.currency || settingsDraft.currency || 'TRY',
    isWaybillReplacement: Boolean(draft?.isWaybillReplacement)
  });

  const items = ensureDraftItems(merged);
  const invoiceTotals = calculateInvoiceTotals(items);
  const primaryProduct = items.map((i) => i.productName).filter(Boolean).join(' · ') || merged.productName || '';

  merged = {
    ...merged,
    items,
    invoiceTotals,
    productName: items.length > 1 ? (items.length <= 3 ? primaryProduct : 'Çoklu Ürün') : primaryProduct,
    quantity: items.reduce((s, i) => s + Number(i.quantity || 0), 0),
    amount: invoiceTotals.payableAmount
  };

  return merged;
};

export const isDraftCustomerComplete = (draft) => {
  const d = syncLegacyCustomerFields(draft || {});
  const nameOk = Boolean(String(d.customerName || d.title || '').trim());
  const cityOk = Boolean(String(d.city || d.customerIl || '').trim());
  const districtOk = Boolean(String(d.district || d.customerIlce || '').trim());
  const digits = resolveVknTckn(d).replace(/\D/g, '');
  const vknOk = d.customerType === 'firma' ? digits.length === 10 : digits.length === 11;
  // e-Fatura mükellefine kesilen belgede vergi dairesi ve adres zorunlu; eksikse
  // EDM/GİB reddeder. Onay kuyruğuna girmeden "Bilgi Eksik"e düşsün.
  // Not: varsayılan senaryo e-Arşiv'dir, bu koşul yalnızca GİB sorgusu
  // "mükellef" dedikten sonra devreye girer.
  // Kullanici karari 09.10.2026: e-Faturada vergi dairesi zorunlu, ADRES
  // isteğe bağlı (Kolaysoft adressiz e-Faturayi kabul ediyor; MMD...0075).
  if (d.scenarioType === 'eFatura') {
    const taxOfficeOk = Boolean(String(d.taxOffice || d.customerTaxOffice || '').trim());
    return nameOk && cityOk && districtOk && vknOk && taxOfficeOk;
  }
  return nameOk && cityOk && districtOk && vknOk;
};

export const getProfessionalDraftMissingLabels = (draft) => {
  const d = syncLegacyCustomerFields(draft || {});
  const missing = [];
  if (!String(d.customerName || d.title || '').trim()) missing.push('Ad/Ünvan eksik');
  const digits = resolveVknTckn(d).replace(/\D/g, '');
  if (d.customerType === 'firma') {
    if (digits.length !== 10) missing.push('VKN eksik');
  } else if (digits.length !== 11) {
    missing.push('TCKN eksik');
  }
  if (!String(d.city || d.customerIl || '').trim()) missing.push('İl eksik');
  if (!String(d.district || d.customerIlce || '').trim()) missing.push('İlçe eksik');
  // e-Fatura mükellefine kesilen faturada vergi dairesi ve adres beklenir;
  // eksikse EDM/GİB belgeyi reddedip geri döner. Onaydan önce yakalarız.
  if (d.scenarioType === 'eFatura') {
    if (!String(d.taxOffice || d.customerTaxOffice || '').trim()) missing.push('Vergi dairesi eksik');
  }
  return missing;
};

/**
 * GİB mükellef sorgusu sonucunu taslağa uygular.
 * Senaryoyu ve müşteri tipini düzeltir, ünvan boşsa GİB'den geleni yazar.
 * Personelin elle girdiği ünvanı EZMEZ.
 */
export const applyRecipientCheckToDraft = (draft = {}, result = null) => {
  if (!result?.ok) return draft;
  const digits = String(result.identifier || '').replace(/\D/g, '');
  const mevcutUnvan = String(draft.customerName || draft.title || '').trim();
  const gibUnvan = String(result.title || '').trim();
  const patch = {
    ...draft,
    scenarioType: result.isEfatura ? 'eFatura' : 'eArsiv',
    recipientCheck: {
      isEfatura: Boolean(result.isEfatura),
      alias: String(result.alias || ''),
      title: gibUnvan,
      unit: String(result.unit || ''),
      identifier: digits,
      testMode: result.testMode !== false,
      checkedAt: new Date().toISOString()
    }
  };
  if (digits.length === 10) patch.customerType = 'firma';
  if (!mevcutUnvan && gibUnvan) {
    patch.customerName = gibUnvan;
    patch.title = gibUnvan;
  }
  return syncLegacyCustomerFields(patch);
};

export const isDraftReadyForSend = (draft) => {
  const normalized = normalizeInvoiceDraft(draft);
  const items = Array.isArray(normalized.items) ? normalized.items : [];
  if (!items.length) return false;

  const hasNamedLines = items.every((item) => Boolean(String(item.productName || '').trim()) && Number(item.quantity) > 0);
  const hasPositiveTotal = Number(normalized.invoiceTotals?.payableAmount) > 0;
  // En az bir satır tutarlı olmalı; işçilik 0 ise sadece ürün satırı yeter
  const hasMoneyLine = items.some((item) => Number(item.lineTotal) > 0);

  if (normalized.invoiceType === 'ozelMatrah') {
    const hasWorkmanship = items.some((item) => item.lineType === 'workmanship' && Number(item.lineTotal) > 0);
    const matrahOk = Number(normalized.invoiceTotals?.ozelMatrahAmount) > 0 || !hasWorkmanship;
    // Has ayrılamadıysa (uyarı) yine kaydedilebilir ama "gönderime hazır" için işçilik veya manuel onay gerekir
    const blockedByMissingMatrah = items.some((item) => item.matrahWarning && !hasWorkmanship);
    if (blockedByMissingMatrah) return false;
    return isDraftCustomerComplete(normalized) && hasNamedLines && hasPositiveTotal && hasMoneyLine && matrahOk;
  }

  return isDraftCustomerComplete(normalized) && hasNamedLines && hasPositiveTotal && hasMoneyLine;
};

export const ensureProfessionalDraftDefaults = (draft = {}, { sube, customerRecord, islem, islemler, settings } = {}) => {
  let next = syncLegacyCustomerFields({ ...draft });

  if (!next.invoiceType) {
    next.invoiceType = 'ozelMatrah';
    next.matrahType = 'Özel Matrah';
  } else if (!next.matrahType) {
    next.matrahType = mapInvoiceTypeToMatrah(next.invoiceType);
  }

  next = normalizeVknTcknFromCustomer(customerRecord, next);

  const branchLocation = getDefaultBranchInvoiceLocation(sube);
  if (!String(next.city || next.customerIl || '').trim() && branchLocation.city) {
    next.city = branchLocation.city;
    next.customerIl = branchLocation.city;
  }
  if (!String(next.district || next.customerIlce || '').trim() && branchLocation.district) {
    next.district = branchLocation.district;
    next.customerIlce = branchLocation.district;
  }

  const resolvedIslemler = (islemler || []).filter((row) => row?.id);
  const primaryIslem = islem?.id ? islem : resolvedIslemler[0] || null;
  const invoiceType = next.invoiceType || 'ozelMatrah';

  if (shouldAutoGenerateItems(next)) {
    const typeOverride = String(next.productTypeOverride || '').trim();
    const rawIslemler = resolvedIslemler.length
      ? resolvedIslemler
      : primaryIslem
        ? [primaryIslem]
        : [];
    const sourceIslemler = typeOverride
      ? rawIslemler.map((row) => ({ ...row, invoiceProductTypeOverride: typeOverride }))
      : rawIslemler;

    if (sourceIslemler.length) {
      next.items =
        invoiceType === 'ozelMatrah'
          ? buildSpecialMatrahItemsFromIslemler(sourceIslemler, settings)
          : sourceIslemler.map((row, idx) => buildItemFromIslem(normalizeIslemForInvoice(row, settings), idx));
      next.autoGeneratedItems = true;
      next.manuallyEditedItems = false;
    }
  }

  return normalizeInvoiceDraft(next, settings);
};

export const finalizeProfessionalDraft = (draft, { islem, islemler, settings, sube, customerRecord } = {}) =>
  ensureProfessionalDraftDefaults(draft, { sube, customerRecord, islem, islemler, settings });

/**
 * MANUEL FATURA — hızlı ekleme (02.10.2026).
 *
 * Personel yalnız ürün tipini ve ÖDENECEK tutarı (KDV dahil) girer; ürün
 * satırı (%0, özel matrah) + işçilik satırı (%20) banka eşleşmesinden gelen
 * faturalardaki motorla üretilir. Tutar, sanal bir havale satışı gibi
 * işlenir; miktar/birim fiyat oradan çıkar.
 *
 * @returns {{ ok: true, items: object[] } | { ok: false, message: string }}
 */
export const buildManualProductPair = ({ productType, amount, settings = null }) => {
  const tip = String(productType || '').trim();
  const tutar = round2(amount);
  if (!tip) return { ok: false, message: 'Ürün tipini seçin.' };
  if (!(tutar > 0)) return { ok: false, message: 'Ödenecek tutarı girin.' };
  if (!isKnownProductTypeOverride(tip, settings)) return { ok: false, message: 'Bilinmeyen ürün tipi.' };

  const sarrafiye = getSarrafiyeProductTypeOptions(settings).find((o) => o.value === tip);
  const hasTl = Number(settings?.liveHasSellPrice) || 0;
  if (sarrafiye && hasTl > 0 && sarrafiye.hasFactor * hasTl > tutar) {
    return {
      ok: false,
      message:
        `${tutar.toLocaleString('tr-TR')} TL, 1 adet ${sarrafiye.label} için yetersiz ` +
        `(has değeri ≈ ${Math.round(sarrafiye.hasFactor * hasTl).toLocaleString('tr-TR')} TL).`
    };
  }

  const sanal = {
    id: `manuel-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    islemTuru: 'Satış',
    kategori: '',
    urun: '',
    gram: 0,
    adet: 0,
    hasFiyati: hasTl,
    toplamTutar: tutar,
    havaleEft: tutar,
    invoiceProductTypeOverride: tip
  };
  const items = buildSpecialMatrahItemsFromIslemler([sanal], settings);
  if (!items.length) return { ok: false, message: 'Satırlar üretilemedi (fiyat bilgisi eksik olabilir).' };
  return { ok: true, items };
};

/** Kayit oncesi: musteri metin alanlarinin bas/son bosluklari ve ic cift bosluk. */
export const trimDraftTextFields = (draft = {}) => {
  const t = (v) => (typeof v === 'string' ? v.replace(/\s+/g, ' ').trim() : v);
  const out = { ...draft };
  ['customerName', 'title', 'city', 'district', 'address', 'taxOffice', 'customerIl', 'customerIlce', 'customerAddress', 'customerTaxOffice', 'vknTckn'].forEach(
    (k) => {
      if (k in out) out[k] = t(out[k]);
    }
  );
  return out;
};
